import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import imageCompression from 'browser-image-compression';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Scale, Plus, ChevronRight, ChevronLeft, Check, Trash2, Edit, X, AlertTriangle, FileDown, Filter, TrendingUp, Package, Percent, UserCheck, Star, Calendar, Clock, Eye, Camera, MapPin, RefreshCw, BarChart3, LineChart } from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import DateInput from '@/components/DateInput';
import ProduccionLayout from '@/components/ProduccionLayout';
import { formatearFecha, formatearFechaHora } from '@/utils/dateUtils';
import { useHojaPesadorCalculations } from '@/hooks/useHojaPesadorCalculations';
import { useHojaPesadorData } from '@/hooks/useHojaPesadorData';
import { useHojaPesadorFilters } from '@/hooks/useHojaPesadorFilters';
import { HojaPesadorKPIs } from '@/components/hoja-pesador/HojaPesadorKPIs';
import { usePermissions } from '@/hooks/usePermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HojaPesadorNuevo({ user, onLogout }) {
  // Hooks personalizados
  const { hasPermission } = usePermissions(user);
  const { 
    hojas, 
    trabajadores, 
    vehiculos, 
    inspectores, 
    liquidaciones,
    loading, 
    fetchData,
    createHoja,
    updateHoja,
    deleteHoja,
    createInspector,
    checkBoletaDuplicada,
    upsertLiquidacion,
    deleteLiquidacion,
  } = useHojaPesadorData();
  
  const {
    busquedaBoleta,
    setBusquedaBoleta,
    filtrosAvanzados,
    setFiltrosAvanzados,
    mostrarFiltros,
    setMostrarFiltros,
    ordenamiento,
    hojasFiltradas,
    divisionesUnicas,
    inspectoresUnicos,
    limpiarFiltros,
    toggleOrdenamiento,
    contarFiltrosActivos
  } = useHojaPesadorFilters(hojas);
  
  const {
    calcularTiempoEnPlanta
  } = useHojaPesadorCalculations();
  
  // Detectar si el usuario es Chofer
  const isChofer = useMemo(() => {
    if (!user?.trabajador_id || !trabajadores.length) return false;
    const userTrabajador = trabajadores.find(t => t.id === user.trabajador_id);
    return userTrabajador?.ocupacion === 'Chofer';
  }, [user, trabajadores]);
  
  // Obtener las últimas 5 boletas del chofer
  const misUltimasBoletas = useMemo(() => {
    if (!isChofer || !user?.trabajador_id) return [];
    const userTrabajador = trabajadores.find(t => t.id === user.trabajador_id);
    if (!userTrabajador) return [];
    
    // Filtrar boletas donde el conductor es el chofer actual
    return hojas
      .filter(h => h.conductor_id === user.trabajador_id || h.conductor_nombre === userTrabajador.nombre_completo)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  }, [isChofer, user, trabajadores, hojas]);
  
  // Estados locales del componente
  const [showForm, setShowForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [editingHojaId, setEditingHojaId] = useState(null);
  const [showNuevoInspectorModal, setShowNuevoInspectorModal] = useState(false);
  const [nuevoInspector, setNuevoInspector] = useState({
    nombre: '',
    primer_apellido: '',
    segundo_apellido: ''
  });

  // Estados para exportar PDF
  const [showExportarModal, setShowExportarModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [columnasSeleccionadas, setColumnasSeleccionadas] = useState([]);
  const [filtrosExportacion, setFiltrosExportacion] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    conductor_id: '',
    vehiculo_id: '',
    division: ''
  });
  
  // Estados para validación de boleta duplicada
  const [validandoBoleta, setValidandoBoleta] = useState(false);
  const [boletaDuplicada, setBoletaDuplicada] = useState(false);
  
  // Estados para imagen de boleta
  const [imagenBoletaFile, setImagenBoletaFile] = useState(null);
  const [imagenBoletaPreview, setImagenBoletaPreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [compressingImage, setCompressingImage] = useState(false);
  
  // Estados para eliminación masiva
  const [selectedHojas, setSelectedHojas] = useState([]);
  const [showDeleteMasivoModal, setShowDeleteMasivoModal] = useState(false);
  const [deletingMasivo, setDeletingMasivo] = useState(false);
  
  // Estados para historial de cambios
  const [historialBoleta, setHistorialBoleta] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [historialBoletaNumero, setHistorialBoletaNumero] = useState('');
  
  // Estado para refrescar datos
  const [refreshing, setRefreshing] = useState(false);
  
  // Estados para pestaña de estadísticas
  const [activeTab, setActiveTab] = useState('registros');
  const currentYear = new Date().getFullYear();
  const [selectedYear1, setSelectedYear1] = useState(currentYear.toString());
  const [selectedYear2, setSelectedYear2] = useState('');
  const [selectedYear3, setSelectedYear3] = useState('');
  
  // Obtener años disponibles de los datos (boletas + liquidaciones históricas)
  const availableYears = useMemo(() => {
    const years = new Set();
    hojas.forEach(hoja => {
      if (hoja.fecha_entrega) {
        const year = new Date(hoja.fecha_entrega).getFullYear();
        if (!isNaN(year)) years.add(year);
      }
    });
    (liquidaciones || []).forEach(l => {
      if (l.año) years.add(l.año);
    });
    // Agregar año actual si no hay datos
    years.add(currentYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [hojas, liquidaciones, currentYear]);
  
  // KPIs comparativos año-vs-año (YTD)
  const kpisYoY = useMemo(() => {
    if (!selectedYear1) return null;
    const yearNum = parseInt(selectedYear1);
    const yearPrev = yearNum - 1;
    const now = new Date();
    // Si el año seleccionado es el actual → cortar al mes actual (YTD real)
    // Si es un año pasado → tomar los 12 meses completos (YoY completo)
    const cutoffMonth = yearNum === currentYear ? now.getMonth() + 1 : 12;

    const acc = { curr: { fruta: 0, coyol: 0, ingreso: 0 }, prev: { fruta: 0, coyol: 0, ingreso: 0 } };

    // Recolectar por año (boletas + liquidaciones con misma regla de mezcla)
    const collect = (targetYear, bucket) => {
      const liqByMonth = {};
      (liquidaciones || [])
        .filter(l => l.año === targetYear && l.mes >= 1 && l.mes <= cutoffMonth)
        .forEach(l => { liqByMonth[l.mes] = l; });

      // Meses con liquidación → esos
      Object.values(liqByMonth).forEach(l => {
        bucket.fruta += l.tm_fruta || 0;
        bucket.coyol += l.tm_coyol || 0;
        bucket.ingreso += l.ingreso_total || 0;
      });

      // Meses SIN liquidación → sumar boletas
      hojas.forEach(h => {
        if (!h.fecha_entrega) return;
        const f = new Date(h.fecha_entrega);
        const m = f.getMonth() + 1;
        if (f.getFullYear() === targetYear && m <= cutoffMonth && !liqByMonth[m]) {
          bucket.fruta += (parseFloat(h.peso_fruta) || 0) / 1000;
          bucket.coyol += (parseFloat(h.peso_coyol) || 0) / 1000;
        }
      });
    };
    collect(yearNum, acc.curr);
    collect(yearPrev, acc.prev);

    const total_curr = acc.curr.fruta + acc.curr.coyol;
    const total_prev = acc.prev.fruta + acc.prev.coyol;
    const pct = (curr, prev) => (prev > 0 ? ((curr - prev) / prev) * 100 : null);

    return {
      yearCurr: yearNum,
      yearPrev,
      cutoffMonth,
      isYTD: yearNum === currentYear,
      total: { curr: total_curr, prev: total_prev, delta: pct(total_curr, total_prev) },
      fruta: { curr: acc.curr.fruta, prev: acc.prev.fruta, delta: pct(acc.curr.fruta, acc.prev.fruta) },
      coyol: { curr: acc.curr.coyol, prev: acc.prev.coyol, delta: pct(acc.curr.coyol, acc.prev.coyol) },
      ingreso: { curr: acc.curr.ingreso, prev: acc.prev.ingreso, delta: pct(acc.curr.ingreso, acc.prev.ingreso) },
    };
  }, [selectedYear1, hojas, liquidaciones, currentYear]);

  // Nombres de meses en español
  const mesesNombres = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  
  // Calcular datos para gráficos (combinando boletas + liquidaciones históricas)
  const chartData = useMemo(() => {
    const selectedYears = [selectedYear1, selectedYear2, selectedYear3].filter(y => y !== '' && y !== 'none');

    // Inicializar datos mensuales
    const monthlyData = mesesNombres.map((mes, index) => ({
      mes,
      mesIndex: index,
    }));

    // Procesar cada año seleccionado
    selectedYears.forEach(year => {
      const yearNum = parseInt(year);
      monthlyData.forEach(item => {
        item[`fruta_${year}`] = 0;
        item[`coyol_${year}`] = 0;
        item[`fruta_boletas_${year}`] = 0;
        item[`coyol_boletas_${year}`] = 0;
        item[`is_liquidacion_${year}`] = false;
      });

      // Sumar desde boletas
      hojas.forEach(hoja => {
        if (hoja.fecha_entrega) {
          const fecha = new Date(hoja.fecha_entrega);
          if (fecha.getFullYear() === yearNum) {
            const mes = fecha.getMonth();
            const pesoFruta = parseFloat(hoja.peso_fruta) || 0;
            const pesoCoyol = parseFloat(hoja.peso_coyol) || 0;
            monthlyData[mes][`fruta_boletas_${year}`] += pesoFruta / 1000;
            monthlyData[mes][`coyol_boletas_${year}`] += pesoCoyol / 1000;
          }
        }
      });

      // Aplicar liquidaciones: si hay liquidación en año/mes → gana la liquidación
      (liquidaciones || [])
        .filter(l => l.año === yearNum)
        .forEach(l => {
          const mesIdx = (l.mes || 0) - 1;
          if (mesIdx >= 0 && mesIdx < 12) {
            monthlyData[mesIdx][`fruta_${year}`] = parseFloat((l.tm_fruta || 0).toFixed(2));
            monthlyData[mesIdx][`coyol_${year}`] = parseFloat((l.tm_coyol || 0).toFixed(2));
            monthlyData[mesIdx][`is_liquidacion_${year}`] = true;
          }
        });

      // Para los meses sin liquidación, usar boletas como valor principal
      monthlyData.forEach(item => {
        if (!item[`is_liquidacion_${year}`]) {
          item[`fruta_${year}`] = parseFloat(item[`fruta_boletas_${year}`].toFixed(2));
          item[`coyol_${year}`] = parseFloat(item[`coyol_boletas_${year}`].toFixed(2));
        }
        item[`fruta_boletas_${year}`] = parseFloat(item[`fruta_boletas_${year}`].toFixed(2));
        item[`coyol_boletas_${year}`] = parseFloat(item[`coyol_boletas_${year}`].toFixed(2));
      });
    });

    return monthlyData;
  }, [hojas, liquidaciones, selectedYear1, selectedYear2, selectedYear3, mesesNombres]);

  // Datos para gráfico de ingresos mensuales (solo de liquidaciones con precio)
  const ingresosChartData = useMemo(() => {
    const selectedYears = [selectedYear1, selectedYear2, selectedYear3].filter(y => y !== '' && y !== 'none');
    const monthlyData = mesesNombres.map((mes, index) => ({ mes, mesIndex: index }));

    selectedYears.forEach(year => {
      const yearNum = parseInt(year);
      monthlyData.forEach(item => {
        item[`ingreso_fruta_${year}`] = 0;
        item[`ingreso_coyol_${year}`] = 0;
      });
      (liquidaciones || [])
        .filter(l => l.año === yearNum)
        .forEach(l => {
          const mesIdx = (l.mes || 0) - 1;
          if (mesIdx >= 0 && mesIdx < 12) {
            monthlyData[mesIdx][`ingreso_fruta_${year}`] = Math.round(l.ingreso_fruta || 0);
            monthlyData[mesIdx][`ingreso_coyol_${year}`] = Math.round(l.ingreso_coyol || 0);
          }
        });
    });
    return monthlyData;
  }, [liquidaciones, selectedYear1, selectedYear2, selectedYear3, mesesNombres]);

  // Años disponibles para cargar liquidaciones (2022 hasta año actual - 1)
  const historicalYears = useMemo(() => {
    const years = [];
    for (let y = 2022; y < currentYear; y++) years.push(y);
    return years;
  }, [currentYear]);

  // Estado para el modal de liquidaciones
  const [showLiquidacionesModal, setShowLiquidacionesModal] = useState(false);
  const [liqEditingYear, setLiqEditingYear] = useState(historicalYears[historicalYears.length - 1] || 2025);
  const [liqRows, setLiqRows] = useState({});  // { [mes]: { tm_fruta, tm_coyol, precio_tm_fruta, precio_tm_coyol } }
  const [savingLiq, setSavingLiq] = useState(false);
  const [pendingYearChange, setPendingYearChange] = useState(null);

  // Detectar si hay cambios sin guardar (comparando liqRows con los datos existentes en BD)
  const isLiqDirty = useMemo(() => {
    if (!showLiquidacionesModal) return false;
    for (let m = 1; m <= 12; m++) {
      const row = liqRows[m] || {};
      const existing = (liquidaciones || []).find(l => l.año === liqEditingYear && l.mes === m);
      const norm = (v) => (v === '' || v === null || v === undefined) ? '' : String(v);
      const dbVals = existing
        ? {
            tm_fruta: norm(existing.tm_fruta),
            tm_coyol: norm(existing.tm_coyol),
            precio_tm_fruta: norm(existing.precio_tm_fruta),
            precio_tm_coyol: norm(existing.precio_tm_coyol),
            ajustes_varios: norm(existing.ajustes_varios),
            descripcion_ajustes: norm(existing.descripcion_ajustes),
          }
        : { tm_fruta: '', tm_coyol: '', precio_tm_fruta: '', precio_tm_coyol: '', ajustes_varios: '', descripcion_ajustes: '' };
      const rowVals = {
        tm_fruta: norm(row.tm_fruta),
        tm_coyol: norm(row.tm_coyol),
        precio_tm_fruta: norm(row.precio_tm_fruta),
        precio_tm_coyol: norm(row.precio_tm_coyol),
        ajustes_varios: norm(row.ajustes_varios),
        descripcion_ajustes: norm(row.descripcion_ajustes),
      };
      for (const k of Object.keys(dbVals)) {
        if (String(parseFloat(dbVals[k]) || dbVals[k]) !== String(parseFloat(rowVals[k]) || rowVals[k])) {
          return true;
        }
      }
    }
    return false;
  }, [liqRows, liquidaciones, liqEditingYear, showLiquidacionesModal]);

  // Cuando se abre el modal o cambia el año, hidratar filas
  useEffect(() => {
    if (!showLiquidacionesModal) return;
    const rows = {};
    for (let m = 1; m <= 12; m++) {
      const existing = (liquidaciones || []).find(l => l.año === liqEditingYear && l.mes === m);
      rows[m] = existing ? {
        tm_fruta: existing.tm_fruta ?? '',
        tm_coyol: existing.tm_coyol ?? '',
        precio_tm_fruta: existing.precio_tm_fruta ?? '',
        precio_tm_coyol: existing.precio_tm_coyol ?? '',
        ajustes_varios: existing.ajustes_varios ?? '',
        descripcion_ajustes: existing.descripcion_ajustes ?? '',
      } : { tm_fruta: '', tm_coyol: '', precio_tm_fruta: '', precio_tm_coyol: '', ajustes_varios: '', descripcion_ajustes: '' };
    }
    setLiqRows(rows);
  }, [showLiquidacionesModal, liqEditingYear, liquidaciones]);

  const canEditLiquidaciones = hasPermission('agricultura.produccion.hoja_pesador.edit');

  const handleSaveAllLiquidaciones = async () => {
    setSavingLiq(true);
    let saved = 0, failed = 0;
    for (let m = 1; m <= 12; m++) {
      const row = liqRows[m] || {};
      const tmF = parseFloat(row.tm_fruta) || 0;
      const tmC = parseFloat(row.tm_coyol) || 0;
      const ajustes = parseFloat(row.ajustes_varios) || 0;
      // No guardamos filas totalmente vacías
      if (tmF === 0 && tmC === 0 && !row.precio_tm_fruta && !row.precio_tm_coyol && ajustes === 0) continue;
      const payload = {
        año: liqEditingYear,
        mes: m,
        tm_fruta: tmF,
        tm_coyol: tmC,
        precio_tm_fruta: row.precio_tm_fruta ? parseFloat(row.precio_tm_fruta) : null,
        precio_tm_coyol: row.precio_tm_coyol ? parseFloat(row.precio_tm_coyol) : null,
        ajustes_varios: ajustes,
        descripcion_ajustes: row.descripcion_ajustes || '',
      };
      const res = await upsertLiquidacion(payload);
      if (res.success) saved++; else failed++;
    }
    setSavingLiq(false);
    if (saved > 0) toast.success(`${saved} mes(es) guardado(s) exitosamente`);
    if (failed > 0) toast.error(`${failed} mes(es) fallaron`);
    if (failed === 0 && saved > 0) setShowLiquidacionesModal(false);
  };

  const handleDeleteLiqRow = async (mes) => {
    const res = await deleteLiquidacion(liqEditingYear, mes);
    if (res.success) toast.success(`Liquidación de ${mesesNombres[mes-1]} ${liqEditingYear} eliminada`);
  };

  // Intento cambiar de año: verificar si hay cambios sin guardar
  const handleYearChange = (newYear) => {
    const nextYear = parseInt(newYear);
    if (isLiqDirty) {
      setPendingYearChange(nextYear);
    } else {
      setLiqEditingYear(nextYear);
    }
  };

  // Confirmación al cambiar de año con cambios sin guardar
  const handleConfirmYearChange = async (action) => {
    if (action === 'save') {
      await handleSaveAllLiquidaciones();
      setLiqEditingYear(pendingYearChange);
    } else if (action === 'discard') {
      setLiqEditingYear(pendingYearChange);
    }
    // 'cancel' simplemente cierra el modal
    setPendingYearChange(null);
  };
  
  // Estado para línea activa en el gráfico
  const [activeLineKey, setActiveLineKey] = useState(null);
  
  // Colores para los años
  const yearColors = {
    0: { fruta: '#10b981', coyol: '#059669' }, // Verde esmeralda
    1: { fruta: '#3b82f6', coyol: '#1d4ed8' }, // Azul
    2: { fruta: '#f59e0b', coyol: '#d97706' }, // Amarillo/Naranja
  };
  
  // Función para refrescar datos
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      toast.success('Datos actualizados');
    } catch (error) {
      toast.error('Error al actualizar datos');
    } finally {
      setRefreshing(false);
    }
  };
  
  // Función para ver historial de una boleta
  const handleVerHistorial = async (hoja) => {
    setHistorialBoletaNumero(hoja.numero_tiquete);
    setLoadingHistorial(true);
    setShowHistorialModal(true);
    try {
      const res = await axios.get(`${API}/hoja-pesador/${hoja.id}/historial`);
      setHistorialBoleta(res.data || []);
    } catch (err) {
      console.error('Error al cargar historial:', err);
      setHistorialBoleta([]);
    } finally {
      setLoadingHistorial(false);
    }
  };
  
  // Función para obtener fecha local en formato YYYY-MM-DD
  const getFechaLocal = () => {
    const ahora = new Date();
    const año = ahora.getFullYear();
    const mes = String(ahora.getMonth() + 1).padStart(2, '0');
    const dia = String(ahora.getDate()).padStart(2, '0');
    return `${año}-${mes}-${dia}`;
  };

  const [formData, setFormData] = useState({
    // Paso 1: Datos Generales
    numero_tiquete: '',
    fecha_entrega: getFechaLocal(),
    hora_ingreso: '',
    hora_salida: '',
    
    // Paso 2: Productor
    conductor_id: '',
    conductor_nombre: '',
    vehiculo_id: '',
    vehiculo_placa: '',
    trazabilidad_fruta: '',
    
    // Paso 3: Producto
    peso_fruta: '',
    cantidad_racimos: '',
    ppr: 0,
    peso_coyol: '',
    cantidad_sacos: '',
    porcentaje_coyol: 0,
    pps: 0,
    peso_neto: 0,
    peso_tara: '',
    peso_bruto: 0,
    
    // Paso 4: Control de Calidad
    cantidad_racimos_evaluados: '',
    cantidad_racimos_pasados: '',
    porcentaje_racimos_pasados: 0,
    cantidad_pinzote_largo: '',
    porcentaje_pinzote_largo: 0,
    cantidad_racimos_verdes: '',
    porcentaje_racimos_verdes: 0,
    cantidad_racimos_sobre_maduros: '',
    porcentaje_racimos_sobre_maduros: 0,
    
    // Inspectores
    inspector: '',
    inspector_2: '',
    
    // División
    division: '',
    
    // Observaciones
    observaciones: '',
    
    usuario_registro: user.full_name || user.username,
  });

  // Función helper para manejar cambios de input
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Función para obtener el nombre del paso actual
  const getStepName = () => {
    if (currentStep === 1) return 'Información Básica';
    if (currentStep === 2) return 'Pesos';
    if (currentStep === 3) return 'Producto';
    if (currentStep === 4) return 'Control de Calidad';
    return '';
  };

  // fetchData se llama automáticamente en el hook useHojaPesadorData
  // Los filtros se manejan automáticamente en el hook useHojaPesadorFilters

  // Validar si el número de boleta ya existe (solo en modo creación)
  useEffect(() => {
    const validarBoletaDuplicada = async () => {
      // Solo validar si:
      // 1. No estamos en modo edición
      // 2. El número de tiquete tiene al menos 3 dígitos
      if (editMode || !formData.numero_tiquete || formData.numero_tiquete.length < 3) {
        setBoletaDuplicada(false);
        return;
      }

      setValidandoBoleta(true);
      try {
        // Buscar en las hojas cargadas si existe el número de tiquete
        const existe = hojas.some(hoja => hoja.numero_tiquete === formData.numero_tiquete);
        setBoletaDuplicada(existe);
      } catch (error) {
        console.error('Error al validar boleta:', error);
        setBoletaDuplicada(false);
      } finally {
        setValidandoBoleta(false);
      }
    };

    // Debounce: esperar 500ms después de que el usuario deje de escribir
    const timeoutId = setTimeout(validarBoletaDuplicada, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.numero_tiquete, hojas, editMode]);

  // fetchData ahora viene del hook useHojaPesadorData

  // Columnas disponibles para exportación
  const columnasDisponibles = [
    { id: 'numero_tiquete', label: 'No. Tiquete' },
    { id: 'fecha_entrega', label: 'Fecha Entrega' },
    { id: 'hora_ingreso', label: 'Hora Ingreso' },
    { id: 'hora_salida', label: 'Hora Salida' },
    { id: 'tiempo_en_planta', label: 'Tiempo en Planta' },
    { id: 'conductor_nombre', label: 'Conductor' },
    { id: 'vehiculo_placa', label: 'Vehículo' },
    { id: 'trazabilidad_fruta', label: 'Trazabilidad' },
    { id: 'peso_fruta', label: 'Peso Fruta (kg)' },
    { id: 'cantidad_racimos', label: 'Cant. Racimos' },
    { id: 'ppr', label: 'PPR' },
    { id: 'peso_coyol', label: 'Peso Coyol (kg)' },
    { id: 'cantidad_sacos', label: 'Cant. Sacos' },
    { id: 'porcentaje_coyol', label: '% Coyol' },
    { id: 'pps', label: 'PPS' },
    { id: 'peso_neto', label: 'Peso Neto (kg)' },
    { id: 'peso_tara', label: 'Peso Tara (kg)' },
    { id: 'peso_bruto', label: 'Peso Bruto (kg)' },
    { id: 'cantidad_racimos_evaluados', label: 'Racimos Evaluados' },
    { id: 'cantidad_racimos_pasados', label: 'Racimos Pasados' },
    { id: 'porcentaje_racimos_pasados', label: '% Pasados' },
    { id: 'cantidad_pinzote_largo', label: 'Pinzote Largo' },
    { id: 'porcentaje_pinzote_largo', label: '% Pinzote' },
    { id: 'cantidad_racimos_verdes', label: 'Racimos Verdes' },
    { id: 'porcentaje_racimos_verdes', label: '% Verdes' },
    { id: 'cantidad_racimos_sobre_maduros', label: 'Racimos Sobre Maduros' },
    { id: 'porcentaje_racimos_sobre_maduros', label: '% Sobre Maduros' },
    { id: 'inspector', label: 'Romanero' },
    { id: 'inspector_2', label: 'Inspector' },
    { id: 'division', label: 'División' },
  ];

  const handleExportarPDF = async () => {
    if (columnasSeleccionadas.length === 0) {
      toast.error('Selecciona al menos una columna para exportar');
      return;
    }

    setExportLoading(true);
    try {
      const response = await axios.post(
        `${API}/hoja-pesador/exportar-pdf`,
        {
          ...filtrosExportacion,
          columnas: columnasSeleccionadas,
          usuario_nombre: user?.full_name || user?.username || 'Usuario',
          user_id: user?.id
        },
        { responseType: 'blob' }
      );

      // Crear link para descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hoja_pesador_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF generado exitosamente');
      setShowExportarModal(false);
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      
      // Manejar error cuando responseType es blob
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          toast.error(errorData.detail || 'Error al generar el PDF');
        } catch {
          toast.error('Error al generar el PDF');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Error al generar el PDF');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const toggleColumna = (columnaId) => {
    setColumnasSeleccionadas(prev => {
      if (prev.includes(columnaId)) {
        return prev.filter(id => id !== columnaId);
      } else {
        return [...prev, columnaId];
      }
    });
  };

  const seleccionarTodasColumnas = () => {
    setColumnasSeleccionadas(columnasDisponibles.map(col => col.id));
  };

  const deseleccionarTodasColumnas = () => {
    setColumnasSeleccionadas([]);
  };

  const seleccionarColumnasFavoritas = () => {
    // Columnas favoritas predefinidas (sin tiempo en planta ni conductor)
    const columnasFavoritas = [
      'numero_tiquete',
      'fecha_entrega',
      'hora_ingreso',
      'hora_salida',
      'vehiculo_placa',
      'trazabilidad_fruta',
      'peso_fruta',
      'cantidad_racimos',
      'peso_coyol',
      'cantidad_sacos',
      'peso_neto',
      'peso_tara',
      'peso_bruto',
      'division'
    ];
    setColumnasSeleccionadas(columnasFavoritas);
  };

  // Calcular PPR cuando cambian peso_fruta o cantidad_racimos
  useEffect(() => {
    if (formData.peso_fruta && formData.cantidad_racimos && parseInt(formData.cantidad_racimos) > 0) {
      const ppr = parseFloat(formData.peso_fruta) / parseInt(formData.cantidad_racimos);
      setFormData(prev => ({ ...prev, ppr: ppr.toFixed(2) }));
    } else {
      setFormData(prev => ({ ...prev, ppr: 0 }));
    }
  }, [formData.peso_fruta, formData.cantidad_racimos]);

  // Calcular Peso Neto
  useEffect(() => {
    const pesoFruta = parseFloat(formData.peso_fruta) || 0;
    const pesoCoyol = parseFloat(formData.peso_coyol) || 0;
    const pesoNeto = pesoFruta + pesoCoyol;
    setFormData(prev => ({ ...prev, peso_neto: pesoNeto > 0 ? pesoNeto : 0 }));
  }, [formData.peso_fruta, formData.peso_coyol]);

  // Calcular % Coyol
  useEffect(() => {
    const pesoCoyol = parseFloat(formData.peso_coyol) || 0;
    const pesoNeto = formData.peso_neto;
    
    if (pesoNeto > 0 && pesoCoyol > 0) {
      const porcentaje = (pesoCoyol / pesoNeto) * 100;
      setFormData(prev => ({ ...prev, porcentaje_coyol: porcentaje.toFixed(2) }));
    } else {
      setFormData(prev => ({ ...prev, porcentaje_coyol: 0 }));
    }
  }, [formData.peso_coyol, formData.peso_neto]);

  // Calcular PPS (Peso Promedio Saco)
  useEffect(() => {
    const pesoCoyol = parseFloat(formData.peso_coyol) || 0;
    const cantidadSacos = parseInt(formData.cantidad_sacos) || 0;
    
    if (pesoCoyol > 0 && cantidadSacos > 0) {
      const pps = pesoCoyol / cantidadSacos;
      setFormData(prev => ({ ...prev, pps: pps.toFixed(2) }));
    } else {
      setFormData(prev => ({ ...prev, pps: 0 }));
    }
  }, [formData.peso_coyol, formData.cantidad_sacos]);

  // Calcular Peso Bruto
  useEffect(() => {
    const pesoNeto = formData.peso_neto;
    const pesoTara = parseFloat(formData.peso_tara) || 0;
    const pesoBruto = pesoNeto + pesoTara;
    setFormData(prev => ({ ...prev, peso_bruto: pesoBruto > 0 ? pesoBruto : 0 }));
  }, [formData.peso_neto, formData.peso_tara]);

  // Calcular porcentajes de control de calidad
  const calculatePercentages = (field, value) => {
    const evaluados = parseInt(formData.cantidad_racimos_evaluados) || 0;
    if (evaluados === 0) {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
      return;
    }

    const cantidad = parseInt(value) || 0;
    const percentage = (cantidad / evaluados) * 100;

    const percentageFieldMap = {
      'cantidad_racimos_pasados': 'porcentaje_racimos_pasados',
      'cantidad_pinzote_largo': 'porcentaje_pinzote_largo',
      'cantidad_racimos_verdes': 'porcentaje_racimos_verdes',
      'cantidad_racimos_sobre_maduros': 'porcentaje_racimos_sobre_maduros',
    };

    setFormData(prev => ({
      ...prev,
      [field]: value,
      [percentageFieldMap[field]]: percentage.toFixed(2),
    }));
  };

  // Recalcular todos los porcentajes cuando cambia la cantidad de racimos evaluados
  useEffect(() => {
    if (currentStep !== 4) return;
    
    const evaluados = parseInt(formData.cantidad_racimos_evaluados) || 0;
    if (evaluados === 0) return;

    const recalculate = (cantidadField) => {
      const cantidad = parseInt(formData[cantidadField]) || 0;
      const percentage = (cantidad / evaluados) * 100;
      return percentage.toFixed(2);
    };

    setFormData(prev => ({
      ...prev,
      porcentaje_racimos_pasados: recalculate('cantidad_racimos_pasados'),
      porcentaje_pinzote_largo: recalculate('cantidad_pinzote_largo'),
      porcentaje_racimos_verdes: recalculate('cantidad_racimos_verdes'),
      porcentaje_racimos_sobre_maduros: recalculate('cantidad_racimos_sobre_maduros'),
    }));
  }, [formData.cantidad_racimos_evaluados, currentStep]);

  const handleAddNewInspector = async () => {
    if (!nuevoInspector.nombre.trim()) {
      toast.error('El nombre del inspector es obligatorio');
      return;
    }

    const result = await createInspector(nuevoInspector);
    if (result.success) {
      const nombreCompleto = result.data.nombre_completo;
      setFormData({ ...formData, inspector: nombreCompleto });
      setShowNuevoInspectorModal(false);
      setNuevoInspector({ nombre: '', primer_apellido: '', segundo_apellido: '' });
    }
  };

  const handleEdit = (hoja) => {
    setFormData({
      numero_tiquete: hoja.numero_tiquete,
      fecha_entrega: hoja.fecha_entrega,
      hora_ingreso: hoja.hora_ingreso,
      hora_salida: hoja.hora_salida,
      conductor_id: hoja.conductor_id,
      conductor_nombre: hoja.conductor_nombre,
      vehiculo_id: hoja.vehiculo_id,
      vehiculo_placa: hoja.vehiculo_placa,
      trazabilidad_fruta: hoja.trazabilidad_fruta || '',
      peso_fruta: hoja.peso_fruta || '',
      cantidad_racimos: hoja.cantidad_racimos || '',
      ppr: hoja.ppr || 0,
      peso_coyol: hoja.peso_coyol || '',
      cantidad_sacos: hoja.cantidad_sacos || '',
      porcentaje_coyol: hoja.porcentaje_coyol || 0,
      pps: hoja.pps || 0,
      peso_neto: hoja.peso_neto || 0,
      peso_tara: hoja.peso_tara || '',
      peso_bruto: hoja.peso_bruto || 0,
      cantidad_racimos_evaluados: hoja.cantidad_racimos_evaluados || '',
      cantidad_racimos_pasados: hoja.cantidad_racimos_pasados || '',
      porcentaje_racimos_pasados: hoja.porcentaje_racimos_pasados || 0,
      cantidad_pinzote_largo: hoja.cantidad_pinzote_largo || '',
      porcentaje_pinzote_largo: hoja.porcentaje_pinzote_largo || 0,
      cantidad_racimos_verdes: hoja.cantidad_racimos_verdes || '',
      porcentaje_racimos_verdes: hoja.porcentaje_racimos_verdes || 0,
      cantidad_racimos_sobre_maduros: hoja.cantidad_racimos_sobre_maduros || '',
      porcentaje_racimos_sobre_maduros: hoja.porcentaje_racimos_sobre_maduros || 0,
      inspector: hoja.inspector,
      inspector_2: hoja.inspector_2 || '',
      division: hoja.division,
      usuario_registro: hoja.usuario_registro,
      imagen_boleta: hoja.imagen_boleta || null, // Incluir la imagen existente
      observaciones: hoja.observaciones || '', // Incluir observaciones existentes
    });
    
    // Limpiar cualquier preview de imagen anterior
    setImagenBoletaFile(null);
    setImagenBoletaPreview(null);
    
    setEditMode(true);
    setEditingHojaId(hoja.id);
    setShowForm(true);
    setCurrentStep(4); // Ir directamente al último paso para facilitar edición
  };


  // Manejar cambio de imagen de boleta con compresión automática
  const handleImagenBoletaChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor selecciona un archivo de imagen válido');
        return;
      }
      
      // Validar tamaño inicial (máximo 15MB antes de comprimir)
      const maxSize = 15 * 1024 * 1024; // 15MB
      if (file.size > maxSize) {
        toast.error('La imagen es demasiado grande. El tamaño máximo es 15MB');
        return;
      }
      
      try {
        setCompressingImage(true);
        
        // Mostrar tamaño original
        const originalSizeMB = (file.size / 1024 / 1024).toFixed(2);
        console.log(`📷 Imagen original: ${originalSizeMB} MB`);
        
        // Opciones de compresión
        const options = {
          maxSizeMB: 0.8,           // Tamaño máximo: 800KB
          maxWidthOrHeight: 1920,   // Resolución máxima: 1920px
          useWebWorker: true,       // Usar Web Worker para no bloquear UI
          fileType: 'image/jpeg',   // Convertir a JPEG para mejor compresión
        };
        
        // Comprimir imagen
        const compressedFile = await imageCompression(file, options);
        
        // Mostrar resultado de compresión
        const compressedSizeMB = (compressedFile.size / 1024 / 1024).toFixed(2);
        const reduction = ((1 - compressedFile.size / file.size) * 100).toFixed(0);
        console.log(`✅ Imagen comprimida: ${compressedSizeMB} MB (${reduction}% reducción)`);
        
        // Notificar al usuario si hubo una reducción significativa
        if (file.size > 1024 * 1024 && reduction > 50) {
          toast.success(`Imagen optimizada: ${originalSizeMB}MB → ${compressedSizeMB}MB`);
        }
        
        // Guardar el archivo comprimido
        setImagenBoletaFile(compressedFile);
        
        // Crear preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagenBoletaPreview(reader.result);
        };
        reader.readAsDataURL(compressedFile);
        
      } catch (error) {
        console.error('Error al comprimir imagen:', error);
        toast.error('Error al procesar la imagen. Intenta con otra imagen.');
      } finally {
        setCompressingImage(false);
      }
    }
  };
  
  // Subir imagen de boleta (intenta Google Drive primero, luego local)
  const uploadImagenBoleta = async () => {
    if (!imagenBoletaFile) return null;
    
    setUploadingImage(true);
    try {
      // Primero verificar si Google Drive está conectado
      const driveStatusResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/drive/status?user_id=${user?.id}`
      );
      const driveStatus = await driveStatusResponse.json();
      
      if (driveStatus.connected) {
        // Subir a Google Drive
        console.log('📁 Subiendo imagen a Google Drive...');
        const formData = new FormData();
        formData.append('file', imagenBoletaFile);
        formData.append('user_id', user?.id);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/hoja-pesador/upload-imagen-drive`,
          {
            method: 'POST',
            body: formData,
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Error al subir a Google Drive');
        }
        
        const data = await response.json();
        console.log('✅ Imagen subida a Google Drive:', data.file_url);
        toast.success('Imagen guardada en Google Drive');
        return data.file_path; // URL pública de Google Drive
      } else {
        // Subir al servidor local (fallback)
        console.log('📁 Google Drive no conectado, usando almacenamiento local...');
        const formData = new FormData();
        formData.append('file', imagenBoletaFile);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/hoja_pesador/upload_boleta`,
          {
            method: 'POST',
            body: formData,
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Error al subir la imagen');
        }
        
        const data = await response.json();
        console.log('✅ Imagen subida al servidor local:', data.file_path);
        toast.info('Imagen guardada localmente (conecta Google Drive para respaldo en la nube)');
        return data.file_path; // Ruta local
      }
    } catch (error) {
      console.error('Error al subir imagen:', error);
      toast.error(`Error al subir la imagen: ${error.message}`);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const resetForm = () => {
    setFormData({
      numero_tiquete: '',
      fecha_entrega: new Date().toISOString().split('T')[0],
      hora_ingreso: '',
      hora_salida: '',
      conductor_id: '',
      conductor_nombre: '',
      vehiculo_id: '',
      vehiculo_placa: '',
      trazabilidad_fruta: '',
      peso_fruta: '',
      cantidad_racimos: '',
      ppr: 0,
      peso_coyol: '',
      cantidad_sacos: '',
      porcentaje_coyol: 0,
      pps: 0,
      peso_neto: 0,
      peso_tara: '',
      peso_bruto: 0,
      cantidad_racimos_evaluados: '',
      cantidad_racimos_pasados: '',
      porcentaje_racimos_pasados: 0,
      cantidad_pinzote_largo: '',
      porcentaje_pinzote_largo: 0,
      cantidad_racimos_verdes: '',
      porcentaje_racimos_verdes: 0,
      cantidad_racimos_sobre_maduros: '',
      porcentaje_racimos_sobre_maduros: 0,
      inspector: '',
      inspector_2: '',
      division: '',
      observaciones: '',
      usuario_registro: user.full_name || user.username,
    });
    setCurrentStep(1);
    setShowForm(false);
    setEditMode(false);
    setEditingHojaId(null);
    // Resetear estados de validación
    setBoletaDuplicada(false);
    setValidandoBoleta(false);
    // Resetear imagen de boleta
    setImagenBoletaFile(null);
    setImagenBoletaPreview(null);
    setUploadingImage(false);
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!formData.numero_tiquete || !formData.fecha_entrega || !formData.hora_ingreso || !formData.hora_salida) {
        toast.error('Complete todos los campos del paso 1');
        return false;
      }
      if (formData.numero_tiquete.length !== 5) {
        toast.error('El número de tiquete debe tener exactamente 5 dígitos');
        return false;
      }
      // Validar que la fecha de entrega no sea futura (no se puede registrar una boleta que aún no existe)
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const fechaEntrega = new Date(formData.fecha_entrega + 'T00:00:00');
      if (fechaEntrega > hoy) {
        toast.error('La fecha de entrega no puede ser futura. No puede registrar una boleta que aún no tiene.');
        return false;
      }
      // Validar que no sea una boleta duplicada (solo en modo creación)
      if (!editMode && boletaDuplicada) {
        toast.error('No se puede continuar: El número de boleta ya está registrado');
        return false;
      }
    }
    if (currentStep === 2) {
      if (!formData.conductor_id || !formData.vehiculo_id) {
        toast.error('Seleccione conductor y vehículo');
        return false;
      }
      // Solo obligatorio en creación, no en edición
      if (!editMode && !formData.trazabilidad_fruta) {
        toast.error('Seleccione la trazabilidad de fruta');
        return false;
      }
    }
    if (currentStep === 3) {
      if (!formData.peso_tara) {
        toast.error('Ingrese el Peso Tara');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.inspector || !formData.division) {
        toast.error('Complete todos los campos obligatorios');
        return;
      }

      // Validar que si hay datos de calidad, debe haber Cantidad Racimos Evaluados
      const tieneRacimosPasados = parseInt(formData.cantidad_racimos_pasados) > 0;
      const tienePinzoteLargo = parseInt(formData.cantidad_pinzote_largo) > 0;
      const tieneRacimosVerdes = parseInt(formData.cantidad_racimos_verdes) > 0;
      const tieneRacimosSobreMaduros = parseInt(formData.cantidad_racimos_sobre_maduros) > 0;
      const tieneAlgunDatoCalidad = tieneRacimosPasados || tienePinzoteLargo || tieneRacimosVerdes || tieneRacimosSobreMaduros;
      const tieneRacimosEvaluados = parseInt(formData.cantidad_racimos_evaluados) > 0;
      const tieneRacimosRegistrados = parseInt(formData.cantidad_racimos) > 0;

      // Si se registraron racimos, es obligatorio ingresar Racimos Evaluados
      if (tieneRacimosRegistrados && !tieneRacimosEvaluados) {
        toast.error('Debe ingresar la Cantidad de Racimos Evaluados');
        return;
      }

      // Si hay datos de calidad específicos, también debe haber Racimos Evaluados
      if (tieneAlgunDatoCalidad && !tieneRacimosEvaluados) {
        toast.error('Debe ingresar la Cantidad de Racimos Evaluados para calcular los porcentajes de calidad');
        return;
      }

      // Subir imagen de boleta si existe una nueva
      let imagen_boleta_path = null;
      if (imagenBoletaFile) {
        toast.info('Subiendo imagen de boleta...', { autoClose: 2000 });
        imagen_boleta_path = await uploadImagenBoleta();
        if (!imagen_boleta_path) {
          // El error ya se muestra en uploadImagenBoleta
          return;
        }
        toast.success('Imagen subida correctamente', { autoClose: 1000 });
      }
      
      // Mostrar que se está guardando
      toast.info('Guardando registro...', { autoClose: 2000 });

      // Determinar qué valor de imagen_boleta enviar
      let imagen_boleta_final;
      if (imagen_boleta_path) {
        // Se subió una nueva imagen
        imagen_boleta_final = imagen_boleta_path;
      } else if (editMode) {
        // Estamos editando: preservar la imagen existente (puede ser null si se eliminó)
        imagen_boleta_final = formData.imagen_boleta;
      } else {
        // Nuevo registro sin imagen
        imagen_boleta_final = null;
      }

      const submitData = {
        ...formData,
        imagen_boleta: imagen_boleta_final,
        peso_fruta: formData.peso_fruta ? parseFloat(formData.peso_fruta) : null,
        cantidad_racimos: formData.cantidad_racimos ? parseInt(formData.cantidad_racimos) : null,
        ppr: formData.ppr ? parseFloat(formData.ppr) : null,
        peso_coyol: formData.peso_coyol ? parseFloat(formData.peso_coyol) : null,
        cantidad_sacos: formData.cantidad_sacos ? parseInt(formData.cantidad_sacos) : null,
        porcentaje_coyol: formData.porcentaje_coyol ? parseFloat(formData.porcentaje_coyol) : null,
        pps: formData.pps ? parseFloat(formData.pps) : null,
        peso_neto: formData.peso_neto ? parseFloat(formData.peso_neto) : null,
        peso_tara: formData.peso_tara ? parseFloat(formData.peso_tara) : null,
        peso_bruto: formData.peso_bruto ? parseFloat(formData.peso_bruto) : null,
        cantidad_racimos_evaluados: formData.cantidad_racimos_evaluados ? parseInt(formData.cantidad_racimos_evaluados) : null,
        cantidad_racimos_pasados: formData.cantidad_racimos_pasados ? parseInt(formData.cantidad_racimos_pasados) : null,
        porcentaje_racimos_pasados: formData.porcentaje_racimos_pasados ? parseFloat(formData.porcentaje_racimos_pasados) : null,
        cantidad_pinzote_largo: formData.cantidad_pinzote_largo ? parseInt(formData.cantidad_pinzote_largo) : null,
        porcentaje_pinzote_largo: formData.porcentaje_pinzote_largo ? parseFloat(formData.porcentaje_pinzote_largo) : null,
        cantidad_racimos_verdes: formData.cantidad_racimos_verdes ? parseInt(formData.cantidad_racimos_verdes) : null,
        porcentaje_racimos_verdes: formData.porcentaje_racimos_verdes ? parseFloat(formData.porcentaje_racimos_verdes) : null,
        cantidad_racimos_sobre_maduros: formData.cantidad_racimos_sobre_maduros ? parseInt(formData.cantidad_racimos_sobre_maduros) : null,
        porcentaje_racimos_sobre_maduros: formData.porcentaje_racimos_sobre_maduros ? parseFloat(formData.porcentaje_racimos_sobre_maduros) : null,
        inspector_2: formData.inspector_2 || null,
        observaciones: formData.observaciones?.trim() || null,
      };

      // Log para depuración
      console.log('📝 Guardando boleta:', {
        editMode,
        imagen_boleta_final,
        formData_imagen: formData.imagen_boleta
      });

      if (editMode && editingHojaId) {
        await axios.put(`${API}/hoja-pesador/${editingHojaId}`, {
          ...submitData,
          user_id: user?.id
        });
        toast.success('Hoja del pesador actualizada exitosamente');
      } else {
        await axios.post(`${API}/hoja-pesador`, {
          ...submitData,
          user_id: user?.id
        });
        toast.success('Hoja del pesador registrada exitosamente');
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar registro');
    }
  };

  const handleDelete = async (hojaId) => {
    if (!window.confirm('¿Está seguro de eliminar este registro?')) return;

    try {
      await axios.delete(`${API}/hoja-pesador/${hojaId}`, {
        data: { user_id: user?.id }
      });
      toast.success('Registro eliminado exitosamente');
      fetchData(); // fetchData viene del hook
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al eliminar registro';
      toast.error(errorMessage);
    }
  };

  // Funciones para eliminación masiva
  const handleSelectHoja = (hojaId) => {
    setSelectedHojas(prev => {
      if (prev.includes(hojaId)) {
        return prev.filter(id => id !== hojaId);
      } else {
        return [...prev, hojaId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedHojas.length === hojasFiltradas.length) {
      setSelectedHojas([]);
    } else {
      setSelectedHojas(hojasFiltradas.map(hoja => hoja.id));
    }
  };

  const handleDeleteMasivo = async () => {
    if (selectedHojas.length === 0) return;
    
    setDeletingMasivo(true);
    let exitosos = 0;
    let fallidos = 0;
    
    try {
      for (const hojaId of selectedHojas) {
        try {
          await axios.delete(`${API}/hoja-pesador/${hojaId}`, {
            data: { user_id: user?.id }
          });
          exitosos++;
        } catch (error) {
          fallidos++;
          console.error(`Error eliminando hoja ${hojaId}:`, error);
        }
      }
      
      if (exitosos > 0) {
        toast.success(`${exitosos} registro(s) eliminado(s) exitosamente`);
      }
      if (fallidos > 0) {
        toast.error(`${fallidos} registro(s) no pudieron eliminarse`);
      }
      
      setSelectedHojas([]);
      setShowDeleteMasivoModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar registros');
    } finally {
      setDeletingMasivo(false);
    }
  };

  // calcularTiempoEnPlanta, limpiarFiltros, divisionesUnicas ahora vienen de los hooks

  // Calcular estadísticas basadas en hojas filtradas
  // Las estadísticas se calculan SIEMPRE sobre los datos filtrados visibles en la tabla
  const estadisticas = useMemo(() => {
    // Usar directamente hojasFiltradas (que ya contiene todos los filtros aplicados)
    const datosParaKPI = hojasFiltradas;
    
    // Total Toneladas Métricas - Separado por Fruta y Coyol
    const totalTMFruta = datosParaKPI.reduce((sum, hoja) => {
      return sum + (parseFloat(hoja.peso_fruta) || 0);
    }, 0) / 1000; // Convertir kg a toneladas
    
    const totalTMCoyol = datosParaKPI.reduce((sum, hoja) => {
      return sum + (parseFloat(hoja.peso_coyol) || 0);
    }, 0) / 1000; // Convertir kg a toneladas

    // Total de Racimos (para Fruta)
    const totalRacimos = datosParaKPI.reduce((sum, hoja) => {
      return sum + (parseInt(hoja.cantidad_racimos) || 0);
    }, 0);

    // Total de Sacos (para Coyol)
    const totalSacos = datosParaKPI.reduce((sum, hoja) => {
      return sum + (parseInt(hoja.cantidad_sacos) || 0);
    }, 0);

    // Total Entregas (según los filtros aplicados)
    const totalEntregas = datosParaKPI.length;

    // % Coyol Real (TM Coyol / Peso Neto Total * 100)
    // Peso Neto Total = TM Fruta + TM Coyol
    const pesoNetoTotal = totalTMFruta + totalTMCoyol;
    const promedioCoyol = pesoNetoTotal > 0 ? (totalTMCoyol / pesoNetoTotal) * 100 : 0;

    // Inspector que más ha castigado (inspector_2, NO romanero)
    // Solo contamos castigos que superan los umbrales:
    // - % Pasados >= 2%
    // - % Pinzote >= 1%
    // - % Verdes >= 2%
    // NO incluimos sobre maduros
    const inspectoresStats = {};
    let totalCastigosGeneral = 0;
    
    datosParaKPI.forEach(hoja => {
      // inspector_2 es el Inspector que castiga (inspector es el Romanero)
      const inspector = hoja.inspector_2 || 'Sin Inspector';
      if (!inspectoresStats[inspector]) {
        inspectoresStats[inspector] = {
          nombre: inspector,
          castigos: 0
        };
      }
      
      // Contar cuántas INSPECCIONES salieron castigadas (no cantidades de racimos)
      // Cada tipo de castigo que supere el umbral cuenta como 1
      let castigosEnEstaHoja = 0;
      
      const porcentajePasados = parseFloat(hoja.porcentaje_racimos_pasados) || 0;
      const porcentajePinzote = parseFloat(hoja.porcentaje_pinzote_largo) || 0;
      const porcentajeVerdes = parseFloat(hoja.porcentaje_racimos_verdes) || 0;
      
      // Contar 1 por cada tipo de castigo que supere el umbral
      if (porcentajePasados >= 2) {
        castigosEnEstaHoja += 1;
      }
      if (porcentajePinzote >= 1) {
        castigosEnEstaHoja += 1;
      }
      if (porcentajeVerdes >= 2) {
        castigosEnEstaHoja += 1;
      }
      
      inspectoresStats[inspector].castigos += castigosEnEstaHoja;
      totalCastigosGeneral += castigosEnEstaHoja;
    });

    // Encontrar el inspector con más castigos
    let inspectorMasCastigos = { nombre: 'Sin datos', castigos: 0 };
    Object.values(inspectoresStats).forEach(inspector => {
      if (inspector.castigos > inspectorMasCastigos.castigos) {
        inspectorMasCastigos = inspector;
      }
    });

    // Si no hay castigos registrados, mostrar mensaje más claro
    if (totalCastigosGeneral === 0) {
      inspectorMasCastigos = { nombre: 'Sin castigos registrados', castigos: 0 };
    }

    // Calcular tiempo promedio en planta
    let tiempoPromedioMinutos = 0;
    let conteoConTiempo = 0;
    
    datosParaKPI.forEach(hoja => {
      if (hoja.hora_ingreso && hoja.hora_salida) {
        try {
          const [ingresoHora, ingresoMin] = hoja.hora_ingreso.split(':').map(Number);
          const [salidaHora, salidaMin] = hoja.hora_salida.split(':').map(Number);
          
          const ingresoMinutos = ingresoHora * 60 + ingresoMin;
          const salidaMinutos = salidaHora * 60 + salidaMin;
          
          let diferenciaMinutos = salidaMinutos - ingresoMinutos;
          if (diferenciaMinutos < 0) diferenciaMinutos += 24 * 60;
          
          tiempoPromedioMinutos += diferenciaMinutos;
          conteoConTiempo++;
        } catch (error) {
          // Ignorar hojas con formato de hora inválido
        }
      }
    });
    
    const tiempoPromedio = conteoConTiempo > 0 ? tiempoPromedioMinutos / conteoConTiempo : 0;
    const horasPromedio = Math.floor(tiempoPromedio / 60);
    const minutosPromedio = Math.round(tiempoPromedio % 60);
    
    let tiempoPromedioTexto = '-';
    if (conteoConTiempo > 0) {
      if (horasPromedio === 0) {
        tiempoPromedioTexto = `${minutosPromedio} min`;
      } else if (minutosPromedio === 0) {
        tiempoPromedioTexto = `${horasPromedio}h`;
      } else {
        tiempoPromedioTexto = `${horasPromedio}h ${minutosPromedio}min`;
      }
    }

    // Trazabilidad - TM por área
    const trazabilidadPNMA = { fruta: 0, coyol: 0 };
    const trazabilidadSinAfectacion = { fruta: 0, coyol: 0 };
    
    datosParaKPI.forEach(hoja => {
      const pesoFruta = parseFloat(hoja.peso_fruta) || 0;
      const pesoCoyol = parseFloat(hoja.peso_coyol) || 0;
      
      if (hoja.trazabilidad_fruta === 'Lote 1 (Área PNMA)' || hoja.trazabilidad_fruta === 'Área PNMA') {
        trazabilidadPNMA.fruta += pesoFruta;
        trazabilidadPNMA.coyol += pesoCoyol;
      } else if (hoja.trazabilidad_fruta === 'Lote 2 (Área sin afectación)' || hoja.trazabilidad_fruta === 'Área sin afectación') {
        trazabilidadSinAfectacion.fruta += pesoFruta;
        trazabilidadSinAfectacion.coyol += pesoCoyol;
      }
    });

    return {
      totalTMFruta: totalTMFruta.toFixed(2),
      totalTMCoyol: totalTMCoyol.toFixed(2),
      totalRacimos,
      totalSacos,
      totalEntregas,
      promedioCoyol: promedioCoyol.toFixed(2),
      tiempoPromedioPlanta: tiempoPromedioTexto,
      inspectorMasCastigos: inspectorMasCastigos.nombre,
      castigosInspector: inspectorMasCastigos.castigos,
      trazabilidad: {
        pnma: {
          fruta: (trazabilidadPNMA.fruta / 1000).toFixed(2),
          coyol: (trazabilidadPNMA.coyol / 1000).toFixed(2),
          total: ((trazabilidadPNMA.fruta + trazabilidadPNMA.coyol) / 1000).toFixed(2)
        },
        sinAfectacion: {
          fruta: (trazabilidadSinAfectacion.fruta / 1000).toFixed(2),
          coyol: (trazabilidadSinAfectacion.coyol / 1000).toFixed(2),
          total: ((trazabilidadSinAfectacion.fruta + trazabilidadSinAfectacion.coyol) / 1000).toFixed(2)
        },
        totales: {
          fruta: ((trazabilidadPNMA.fruta + trazabilidadSinAfectacion.fruta) / 1000).toFixed(2),
          coyol: ((trazabilidadPNMA.coyol + trazabilidadSinAfectacion.coyol) / 1000).toFixed(2)
        }
      }
    };
  }, [hojasFiltradas]);

  const getStepTitle = () => {
    if (currentStep === 1) return 'Datos Generales';
    if (currentStep === 2) return 'Productor';
    if (currentStep === 3) return 'Producto';
    if (currentStep === 4) return 'Control de Calidad';
    return '';
  };

  if (loading) {
    return (
      <ProduccionLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Cargando...</p>
        </div>
      </ProduccionLayout>
    );
  }

  // ============ VISTA ESPECIAL PARA CHOFERES (Solo la pantalla inicial, NO el formulario) ============
  // Cuando showForm es true, usamos el mismo formulario del admin para mantener consistencia
  if (isChofer && !showForm) {
    return (
      <ProduccionLayout user={user} onLogout={onLogout}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key="chofer-home"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
                {/* Encabezado */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                    <Scale className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Hoja del Pesador</h1>
                  <p className="text-gray-500 mt-1">Registra tus boletas de entrega</p>
                </div>

                {/* Botón Nueva Boleta */}
                <div className="flex justify-center mb-8">
                  <Button
                    onClick={() => {
                      resetForm();
                      setShowForm(true);
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6"
                    size="lg"
                  >
                    <Plus className="w-6 h-6 mr-2" />
                    Nueva Boleta
                  </Button>
                </div>

                {/* Últimas 5 boletas registradas */}
                {misUltimasBoletas.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center">
                        <Clock className="w-5 h-5 mr-2 text-emerald-600" />
                        Últimas 5 boletas registradas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {misUltimasBoletas.map((boleta) => (
                          <div 
                            key={boleta.id}
                            className="p-3 bg-gray-50 rounded-lg border"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="bg-emerald-100 text-emerald-700 font-bold px-3 py-1 rounded">
                                #{boleta.numero_tiquete}
                              </div>
                              <div className="flex items-center gap-2">
                                {boleta.imagen_boleta && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-green-600"
                                    onClick={() => {
                                      let imageUrl = boleta.imagen_boleta;
                                      if (imageUrl.startsWith('https://')) {
                                        window.open(imageUrl, '_blank');
                                      } else {
                                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api${imageUrl}`, '_blank');
                                      }
                                    }}
                                    title="Ver imagen"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                  <Check className="w-3 h-3 mr-1" />
                                  Registrada
                                </Badge>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">Fecha entrega:</span>
                                <span className="ml-1 font-medium text-gray-900">{formatearFecha(boleta.fecha_entrega)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Fecha registro:</span>
                                <span className="ml-1 font-medium text-gray-900">
                                  {boleta.fecha_registro ? new Date(boleta.fecha_registro).toLocaleString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Chofer:</span>
                                <span className="ml-1 font-medium text-gray-900">{boleta.conductor_nombre || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Registrado por:</span>
                                <span className="ml-1 font-medium text-gray-900">{boleta.usuario_registro || 'N/A'}</span>
                              </div>
                            </div>
                            <div className="mt-2 text-sm text-gray-500">
                              {boleta.cantidad_racimos || 0} racimos • {boleta.vehiculo_placa}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {misUltimasBoletas.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center text-gray-500">
                      <Scale className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Aún no tienes boletas registradas</p>
                      <p className="text-sm mt-1">Haz clic en &quot;Nueva Boleta&quot; para crear tu primer registro</p>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
          </AnimatePresence>
        </div>
      </ProduccionLayout>
    );
  }
  // ============ FIN VISTA PARA CHOFERES ============

  return (
    <ProduccionLayout user={user} onLogout={onLogout}>
    <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <AnimatePresence mode="wait">
      {!showForm ? (
        <motion.div
          key="tabla"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Scale className="w-7 h-7 mr-3" />
              Hoja del Pesador
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Pestañas: Registros | Estadísticas */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
                <TabsTrigger value="registros" className="flex items-center gap-2">
                  <Scale className="w-4 h-4" />
                  Registros
                </TabsTrigger>
                <TabsTrigger value="estadisticas" className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Estadísticas
                </TabsTrigger>
              </TabsList>
              
              {/* TAB: Registros */}
              <TabsContent value="registros">
            {/* Tarjetas de Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              {/* Total TM - Fruta, Coyol y Total */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-emerald-700 mb-2">Total TM</p>
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-base font-bold text-emerald-900 whitespace-nowrap">{estadisticas.totalTMFruta}</span>
                            <span className="text-xs text-emerald-600 whitespace-nowrap">TM Fruta</span>
                            <span className="text-xs text-emerald-500 hidden sm:inline">|</span>
                            <span className="text-base font-bold text-emerald-900 whitespace-nowrap">{estadisticas.totalRacimos?.toLocaleString()}</span>
                            <span className="text-xs text-emerald-600 whitespace-nowrap">Racimos</span>
                          </div>
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-base font-bold text-emerald-900 whitespace-nowrap">{estadisticas.totalTMCoyol}</span>
                            <span className="text-xs text-emerald-600 whitespace-nowrap">TM Coyol</span>
                            <span className="text-xs text-emerald-500 hidden sm:inline">|</span>
                            <span className="text-base font-bold text-emerald-900 whitespace-nowrap">{estadisticas.totalSacos?.toLocaleString()}</span>
                            <span className="text-xs text-emerald-600 whitespace-nowrap">Sacos</span>
                          </div>
                          <div className="pt-1 mt-1 border-t border-emerald-300">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xl font-bold text-emerald-900 whitespace-nowrap">
                                {(parseFloat(estadisticas.totalTMFruta) + parseFloat(estadisticas.totalTMCoyol)).toFixed(2)}
                              </span>
                              <span className="text-xs font-semibold text-emerald-700">TM Total</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 bg-emerald-200 rounded-full flex-shrink-0">
                        <TrendingUp className="w-6 h-6 text-emerald-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* % Coyol Entregado - MOVIDO AQUÍ */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Card className={`bg-gradient-to-br ${
                  parseFloat(estadisticas.promedioCoyol) < 5 ? 'from-red-50 to-red-100 border-red-200' :
                  parseFloat(estadisticas.promedioCoyol) < 7 ? 'from-yellow-50 to-yellow-100 border-yellow-200' :
                  'from-green-50 to-green-100 border-green-200'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-medium ${
                          parseFloat(estadisticas.promedioCoyol) < 5 ? 'text-red-700' :
                          parseFloat(estadisticas.promedioCoyol) < 7 ? 'text-yellow-700' :
                          'text-green-700'
                        }`}>% Coyol Entregado</p>
                        <p className={`text-2xl font-bold ${
                          parseFloat(estadisticas.promedioCoyol) < 5 ? 'text-red-900' :
                          parseFloat(estadisticas.promedioCoyol) < 7 ? 'text-yellow-900' :
                          'text-green-900'
                        }`}>{estadisticas.promedioCoyol}%</p>
                        <p className={`text-xs mt-1 ${
                          parseFloat(estadisticas.promedioCoyol) < 5 ? 'text-red-600' :
                          parseFloat(estadisticas.promedioCoyol) < 7 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {parseFloat(estadisticas.promedioCoyol) < 5 ? 'Crítico' :
                           parseFloat(estadisticas.promedioCoyol) < 7 ? 'Advertencia' :
                           'Normal'}
                        </p>
                      </div>
                      <div className={`p-3 rounded-full ${
                        parseFloat(estadisticas.promedioCoyol) < 5 ? 'bg-red-200' :
                        parseFloat(estadisticas.promedioCoyol) < 7 ? 'bg-yellow-200' :
                        'bg-green-200'
                      }`}>
                        <Percent className={`w-6 h-6 ${
                          parseFloat(estadisticas.promedioCoyol) < 5 ? 'text-red-700' :
                          parseFloat(estadisticas.promedioCoyol) < 7 ? 'text-yellow-700' :
                          'text-green-700'
                        }`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Total Entregas - MOVIDO AQUÍ */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-700">Total Entregas</p>
                        <p className="text-2xl font-bold text-blue-900">{estadisticas.totalEntregas}</p>
                        <p className="text-xs text-blue-600 mt-1">Registros</p>
                      </div>
                      <div className="p-3 bg-blue-200 rounded-full">
                        <Package className="w-6 h-6 text-blue-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Tiempo en Planta - NUEVO KPI */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-orange-700">Tiempo en Planta</p>
                        <p className="text-2xl font-bold text-orange-900">{estadisticas.tiempoPromedioPlanta}</p>
                        <p className="text-xs text-orange-600 mt-1">Promedio</p>
                      </div>
                      <div className="p-3 bg-orange-200 rounded-full">
                        <Clock className="w-6 h-6 text-orange-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Inspector que más castiga */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-purple-700">Inspector + Crítico</p>
                        <p className={`font-bold text-purple-900 truncate ${
                          estadisticas.inspectorMasCastigos.includes('Sin') ? 'text-sm' : 'text-lg'
                        }`} title={estadisticas.inspectorMasCastigos}>
                          {estadisticas.inspectorMasCastigos}
                        </p>
                        <p className="text-xs text-purple-600 mt-1">
                          {estadisticas.castigosInspector > 0 
                            ? `${estadisticas.castigosInspector} castigos` 
                            : 'Periodo seleccionado'}
                        </p>
                      </div>
                      <div className="p-3 bg-purple-200 rounded-full flex-shrink-0 ml-2">
                        <UserCheck className="w-6 h-6 text-purple-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* KPI Trazabilidad */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
                className="md:col-span-2"
              >
                <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-teal-700 mb-3">Trazabilidad</p>
                        <div className="grid grid-cols-2 gap-4">
                          {/* Lote 1 (Área PNMA) */}
                          <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                            <p className="text-xs font-semibold text-amber-700 mb-1">Lote 1 (Área PNMA)</p>
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-amber-600">Fruta:</span>
                                <span className="font-bold text-amber-800">{estadisticas.trazabilidad?.pnma?.fruta || '0.00'} TM</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-amber-600">Coyol:</span>
                                <span className="font-bold text-amber-800">{estadisticas.trazabilidad?.pnma?.coyol || '0.00'} TM</span>
                              </div>
                              <div className="flex justify-between text-xs pt-1 mt-1 border-t border-amber-200">
                                <span className="text-amber-700 font-medium">Total:</span>
                                <span className="font-bold text-amber-900">{estadisticas.trazabilidad?.pnma?.total || '0.00'} TM</span>
                              </div>
                            </div>
                          </div>
                          {/* Lote 2 (Área sin afectación) */}
                          <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-200">
                            <p className="text-xs font-semibold text-emerald-700 mb-1">Lote 2 (Área sin afectación)</p>
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-emerald-600">Fruta:</span>
                                <span className="font-bold text-emerald-800">{estadisticas.trazabilidad?.sinAfectacion?.fruta || '0.00'} TM</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-emerald-600">Coyol:</span>
                                <span className="font-bold text-emerald-800">{estadisticas.trazabilidad?.sinAfectacion?.coyol || '0.00'} TM</span>
                              </div>
                              <div className="flex justify-between text-xs pt-1 mt-1 border-t border-emerald-200">
                                <span className="text-emerald-700 font-medium">Total:</span>
                                <span className="font-bold text-emerald-900">{estadisticas.trazabilidad?.sinAfectacion?.total || '0.00'} TM</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Totales Generales */}
                        <div className="mt-3 pt-2 border-t border-teal-200 grid grid-cols-2 gap-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-teal-700 font-medium">Total Fruta:</span>
                            <span className="font-bold text-teal-800">{estadisticas.trazabilidad?.totales?.fruta || '0.00'} TM</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-teal-700 font-medium">Total Coyol:</span>
                            <span className="font-bold text-teal-800">{estadisticas.trazabilidad?.totales?.coyol || '0.00'} TM</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 bg-teal-200 rounded-full flex-shrink-0 ml-3">
                        <MapPin className="w-6 h-6 text-teal-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
            {/* Barra de Búsqueda y Acciones - RESPONSIVE */}
            <div className="space-y-3 mb-6">
              {/* Primera fila: Campo de Búsqueda (full width en móvil) */}
              <div className="w-full">
                <Label htmlFor="busquedaBoleta" className="text-sm font-medium text-gray-700 mb-2 block md:hidden">
                  Buscar Boleta
                </Label>
                <Input
                  id="busquedaBoleta"
                  type="text"
                  placeholder="Buscar por No. de Boleta..."
                  value={busquedaBoleta}
                  onChange={(e) => setBusquedaBoleta(e.target.value)}
                  className="w-full text-base"
                />
              </div>

              {/* Segunda fila: Botones de control y acciones */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                {/* Grupo 1: Filtros y Ordenar (izquierda en desktop, stacked en móvil) */}
                <div className="flex gap-2 flex-1">
                  {/* Botón Filtros (con contador) */}
                  <Button
                    variant="outline"
                    onClick={() => setMostrarFiltros(!mostrarFiltros)}
                    className="border-gray-300 flex-1 sm:flex-initial"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Filtros</span>
                    <span className="sm:hidden">Filtros</span>
                    {(filtrosAvanzados.fechaDesde || filtrosAvanzados.fechaHasta || 
                      filtrosAvanzados.conductorId || filtrosAvanzados.vehiculoId || 
                      filtrosAvanzados.division || filtrosAvanzados.inspector) && (
                      <Badge className="ml-2 bg-emerald-600">
                        {[filtrosAvanzados.fechaDesde, filtrosAvanzados.fechaHasta, 
                          filtrosAvanzados.conductorId, filtrosAvanzados.vehiculoId, 
                          filtrosAvanzados.division, filtrosAvanzados.inspector].filter(Boolean).length}
                      </Badge>
                    )}
                  </Button>

                  {/* Botón Ordenar por Fecha */}
                  <Button
                    variant="outline"
                    onClick={toggleOrdenamiento}
                    className="border-gray-300 flex-1 sm:flex-initial"
                    title={ordenamiento === 'desc' ? 'Ordenar: Más recientes primero' : 'Ordenar: Más antiguas primero'}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="hidden lg:inline">{ordenamiento === 'desc' ? 'Más recientes' : 'Más antiguas'}</span>
                    <span className="lg:hidden">Orden</span>
                  </Button>
                </div>

                {/* Grupo 2: Botones de Acción (derecha en desktop, full width en móvil) */}
                <div className="flex gap-2">
                  {/* Botón Eliminar Masivo - solo visible cuando hay seleccionados */}
                  {hasPermission('agricultura.produccion.hoja_pesador.delete') && selectedHojas.length > 0 && (
                    <Button 
                      onClick={() => setShowDeleteMasivoModal(true)} 
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-50 flex-1 sm:flex-initial"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Eliminar ({selectedHojas.length})</span>
                      <span className="sm:hidden">{selectedHojas.length}</span>
                    </Button>
                  )}
                  
                  {hasPermission('agricultura.produccion.hoja_pesador.export') && (
                    <Button 
                      onClick={() => setShowExportarModal(true)} 
                      variant="outline"
                      className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 flex-1 sm:flex-initial"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Exportar PDF</span>
                      <span className="sm:hidden">Exportar</span>
                    </Button>
                  )}
                  
                  {hasPermission('agricultura.produccion.hoja_pesador.create') && (
                    <Button 
                      onClick={() => setShowForm(true)} 
                      className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-initial"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Nuevo Registro</span>
                      <span className="sm:hidden">Nuevo</span>
                    </Button>
                  )}
                </div>
                
                {/* Botón Refrescar - Siempre visible */}
                <Button 
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="border-emerald-300 hover:bg-emerald-50 ml-2"
                  title="Refrescar datos"
                >
                  <RefreshCw className={`w-5 h-5 text-emerald-600 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Filtros Avanzados */}
            {mostrarFiltros && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center mb-4">
                <Filter className="w-5 h-5 text-gray-700 mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">Filtros</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Fecha Desde */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Fecha Desde</Label>
                  <Input
                    type="date"
                    value={filtrosAvanzados.fechaDesde}
                    onChange={(e) => setFiltrosAvanzados({...filtrosAvanzados, fechaDesde: e.target.value})}
                    className="w-full"
                  />
                </div>

                {/* Fecha Hasta */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Fecha Hasta</Label>
                  <Input
                    type="date"
                    value={filtrosAvanzados.fechaHasta}
                    onChange={(e) => setFiltrosAvanzados({...filtrosAvanzados, fechaHasta: e.target.value})}
                    className="w-full"
                  />
                </div>

                {/* Conductor */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Conductor</Label>
                  <Select
                    value={filtrosAvanzados.conductorId || undefined}
                    onValueChange={(value) => setFiltrosAvanzados({...filtrosAvanzados, conductorId: value === 'todos' ? '' : value})}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Todos los conductores" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los conductores</SelectItem>
                      {trabajadores
                        .filter(t => t.ocupacion === 'Chofer' && t.status === 'Activo')
                        .map(conductor => (
                          <SelectItem key={conductor.id} value={conductor.id}>
                            {conductor.nombre} {conductor.primer_apellido}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Vehículo */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Vehículo</Label>
                  <Select
                    value={filtrosAvanzados.vehiculoId || undefined}
                    onValueChange={(value) => setFiltrosAvanzados({...filtrosAvanzados, vehiculoId: value === 'todos' ? '' : value})}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Todos los vehículos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los vehículos</SelectItem>
                      {vehiculos
                        .filter(v => v.estado_actual === 'Activo')
                        .map(vehiculo => (
                          <SelectItem key={vehiculo.id} value={vehiculo.id}>
                            {vehiculo.placa} - {vehiculo.marca}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* División */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">División</Label>
                  <Select
                    value={filtrosAvanzados.division || undefined}
                    onValueChange={(value) => setFiltrosAvanzados({...filtrosAvanzados, division: value === 'todas' ? '' : value})}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Todas las divisiones" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todas">Todas las divisiones</SelectItem>
                      {divisionesUnicas.map(division => (
                        <SelectItem key={division} value={division}>
                          {division}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Inspector */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Inspector</Label>
                  <Select
                    value={filtrosAvanzados.inspector || undefined}
                    onValueChange={(value) => setFiltrosAvanzados({...filtrosAvanzados, inspector: value === 'todos' ? '' : value})}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Todos los inspectores" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los inspectores</SelectItem>
                      {inspectores.map(inspector => (
                        <SelectItem key={inspector.id} value={inspector.nombre_completo}>
                          {inspector.nombre_completo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Botón Limpiar Filtros y Resumen */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">Mostrando:</span> {hojasFiltradas.length} {hojasFiltradas.length === 1 ? 'registro' : 'registros'}
                  {hojasFiltradas.length !== hojas.length && (
                    <span className="text-gray-500 ml-2">(de {hojas.length} total)</span>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={limpiarFiltros}
                  className="text-gray-700 border-gray-300 hover:bg-gray-100"
                >
                  Limpiar Filtros
                </Button>
              </div>
            </motion.div>
            )}

            {/* Resumen de Resultados (siempre visible) */}
            <div className="mb-4 text-sm text-gray-600">
              <span className="font-semibold">Mostrando:</span> {hojasFiltradas.length} {hojasFiltradas.length === 1 ? 'registro' : 'registros'}
              {hojasFiltradas.length !== hojas.length && (
                <span className="text-gray-500 ml-2">(de {hojas.length} total)</span>
              )}
            </div>
            {/* TABLA */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* Checkbox para seleccionar todos */}
                    {hasPermission('agricultura.produccion.hoja_pesador.delete') && (
                      <TableHead className="w-[50px] sticky left-0 bg-white z-10">
                        <input
                          type="checkbox"
                          checked={selectedHojas.length === hojasFiltradas.length && hojasFiltradas.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          title="Seleccionar todos"
                        />
                      </TableHead>
                    )}
                    <TableHead className="min-w-[120px]">Usuario</TableHead>
                    <TableHead className="min-w-[150px]">Fecha y Hora Registro</TableHead>
                    <TableHead className="min-w-[100px]">No. Tiquete</TableHead>
                    <TableHead className="min-w-[110px]">Fecha Entrega</TableHead>
                    <TableHead className="min-w-[100px]">Hora Ingreso</TableHead>
                    <TableHead className="min-w-[100px]">Hora Salida</TableHead>
                    <TableHead className="min-w-[140px]">Tiempo en Planta</TableHead>
                    <TableHead className="min-w-[150px]">Conductor</TableHead>
                    <TableHead className="min-w-[100px]">Vehículo</TableHead>
                    <TableHead className="min-w-[140px]">Trazabilidad</TableHead>
                    <TableHead className="min-w-[100px]">Peso Fruta</TableHead>
                    <TableHead className="min-w-[110px]">Cant. Racimos</TableHead>
                    <TableHead className="min-w-[80px]">PPR</TableHead>
                    <TableHead className="min-w-[100px]">Peso Coyol</TableHead>
                    <TableHead className="min-w-[110px]">Cant. Sacos</TableHead>
                    <TableHead className="min-w-[100px]">% Coyol</TableHead>
                    <TableHead className="min-w-[80px]">PPS</TableHead>
                    <TableHead className="min-w-[100px]">Peso Neto</TableHead>
                    <TableHead className="min-w-[100px]">Peso Tara</TableHead>
                    <TableHead className="min-w-[100px]">Peso Bruto</TableHead>
                    <TableHead className="min-w-[130px]">Racimos Evaluados</TableHead>
                    <TableHead className="min-w-[130px]">Racimos Pasados</TableHead>
                    <TableHead className="min-w-[110px]">% Pasados</TableHead>
                    <TableHead className="min-w-[130px]">Pinzote Largo</TableHead>
                    <TableHead className="min-w-[130px]">% Pinzote</TableHead>
                    <TableHead className="min-w-[130px]">Racimos Verdes</TableHead>
                    <TableHead className="min-w-[110px]">% Verdes</TableHead>
                    <TableHead className="min-w-[150px]">Racimos Sobre Maduros</TableHead>
                    <TableHead className="min-w-[130px]">% Sobre Maduros</TableHead>
                    <TableHead className="min-w-[120px]">Romanero</TableHead>
                    <TableHead className="min-w-[120px]">Inspector</TableHead>
                    <TableHead className="min-w-[180px]">División</TableHead>
                    <TableHead className="min-w-[100px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hojasFiltradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={33} className="text-center text-gray-500">
                        No hay registros
                      </TableCell>
                    </TableRow>
                  ) : (
                    hojasFiltradas.map((hoja, index) => (
                      <motion.tr
                        key={hoja.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        className={`border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted ${
                          selectedHojas.includes(hoja.id) ? 'bg-red-50' : ''
                        }`}
                      >
                        {/* Checkbox para seleccionar fila */}
                        {hasPermission('agricultura.produccion.hoja_pesador.delete') && (
                          <TableCell className="sticky left-0 bg-white z-10">
                            <input
                              type="checkbox"
                              checked={selectedHojas.includes(hoja.id)}
                              onChange={() => handleSelectHoja(hoja.id)}
                              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                            />
                          </TableCell>
                        )}
                        <TableCell>{hoja.usuario_registro}</TableCell>
                        <TableCell>{formatearFechaHora(hoja.fecha_registro)}</TableCell>
                        <TableCell className="font-medium">{hoja.numero_tiquete}</TableCell>
                        <TableCell>{formatearFecha(hoja.fecha_entrega)}</TableCell>
                        <TableCell>{hoja.hora_ingreso}</TableCell>
                        <TableCell>{hoja.hora_salida}</TableCell>
                        <TableCell className="font-semibold text-blue-600">
                          {calcularTiempoEnPlanta(hoja.hora_ingreso, hoja.hora_salida)}
                        </TableCell>
                        <TableCell>{hoja.conductor_nombre}</TableCell>
                        <TableCell>{hoja.vehiculo_placa}</TableCell>
                        <TableCell className={`font-medium ${
                          (hoja.trazabilidad_fruta === 'Lote 1 (Área PNMA)' || hoja.trazabilidad_fruta === 'Área PNMA') ? 'text-amber-600' : 'text-emerald-600'
                        }`}>
                          {hoja.trazabilidad_fruta || '-'}
                        </TableCell>
                        <TableCell>{hoja.peso_fruta || '-'}</TableCell>
                        <TableCell>{hoja.cantidad_racimos || '-'}</TableCell>
                        <TableCell className="font-semibold text-emerald-600">{hoja.ppr || '-'}</TableCell>
                        <TableCell>{hoja.peso_coyol || '-'}</TableCell>
                        <TableCell>{hoja.cantidad_sacos || '-'}</TableCell>
                        <TableCell className={`font-semibold ${
                          !hoja.porcentaje_coyol ? '' : 
                          parseFloat(hoja.porcentaje_coyol) < 5 ? 'text-red-600' : 
                          parseFloat(hoja.porcentaje_coyol) < 7 ? 'text-yellow-600' : 
                          'text-emerald-600'
                        }`}>
                          {hoja.porcentaje_coyol ? `${hoja.porcentaje_coyol}%` : '-'}
                        </TableCell>
                        <TableCell className={`font-semibold ${parseFloat(hoja.pps) < 20 && hoja.pps ? 'text-red-600' : 'text-emerald-600'}`}>{hoja.pps || '-'}</TableCell>
                        <TableCell>{hoja.peso_neto || '-'}</TableCell>
                        <TableCell>{hoja.peso_tara || '-'}</TableCell>
                        <TableCell>{hoja.peso_bruto || '-'}</TableCell>
                        <TableCell>{hoja.cantidad_racimos_evaluados || '-'}</TableCell>
                        <TableCell>{hoja.cantidad_racimos_pasados || '-'}</TableCell>
                        <TableCell className={`font-semibold ${
                          !hoja.porcentaje_racimos_pasados ? '' : 
                          parseFloat(hoja.porcentaje_racimos_pasados) >= 2 ? 'text-red-600' : 
                          'text-green-600'
                        }`}>
                          {hoja.porcentaje_racimos_pasados ? `${hoja.porcentaje_racimos_pasados}%` : '-'}
                        </TableCell>
                        <TableCell>{hoja.cantidad_pinzote_largo || '-'}</TableCell>
                        <TableCell className={`font-semibold ${
                          !hoja.porcentaje_pinzote_largo ? '' : 
                          parseFloat(hoja.porcentaje_pinzote_largo) >= 1 ? 'text-red-600' : 
                          'text-green-600'
                        }`}>
                          {hoja.porcentaje_pinzote_largo ? `${hoja.porcentaje_pinzote_largo}%` : '-'}
                        </TableCell>
                        <TableCell>{hoja.cantidad_racimos_verdes || '-'}</TableCell>
                        <TableCell className={`font-semibold ${
                          !hoja.porcentaje_racimos_verdes ? '' : 
                          parseFloat(hoja.porcentaje_racimos_verdes) >= 2 ? 'text-red-600' : 
                          'text-green-600'
                        }`}>
                          {hoja.porcentaje_racimos_verdes ? `${hoja.porcentaje_racimos_verdes}%` : '-'}
                        </TableCell>
                        <TableCell>{hoja.cantidad_racimos_sobre_maduros || '-'}</TableCell>
                        <TableCell>{hoja.porcentaje_racimos_sobre_maduros ? `${hoja.porcentaje_racimos_sobre_maduros}%` : '-'}</TableCell>
                        <TableCell>{hoja.inspector}</TableCell>
                        <TableCell>{hoja.inspector_2 || '-'}</TableCell>
                        <TableCell>{hoja.division}</TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            {hasPermission('agricultura.produccion.hoja_pesador.view_boleta') && hoja.imagen_boleta && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => {
                                  // Determinar si es URL de Google Drive o local
                                  let imageUrl = hoja.imagen_boleta;
                                  
                                  // Si es URL de Google Drive (empieza con https://), abrir directamente
                                  if (imageUrl.startsWith('https://')) {
                                    console.log('Abriendo imagen de Google Drive:', imageUrl);
                                    window.open(imageUrl, '_blank');
                                    return;
                                  }
                                  
                                  // Es ruta local, agregar prefijo del backend
                                  imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api${imageUrl}`;
                                  console.log('Abriendo imagen local:', imageUrl);
                                  window.open(imageUrl, '_blank');
                                }}
                                title="Ver imagen de boleta"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {hasPermission('agricultura.produccion.hoja_pesador.edit') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600"
                                onClick={() => handleEdit(hoja)}
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-purple-600"
                              onClick={() => handleVerHistorial(hoja)}
                              title="Ver historial"
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                            {hasPermission('agricultura.produccion.hoja_pesador.delete') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600"
                                onClick={() => handleDelete(hoja.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
              </TabsContent>
              
              {/* TAB: Estadísticas */}
              <TabsContent value="estadisticas">
                <div className="space-y-6">
                  {/* Selectores de años + Botón liquidaciones */}
                  <div className="flex flex-wrap gap-4 items-end p-4 bg-gray-50 rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Año Principal *</Label>
                      <Select value={selectedYear1} onValueChange={setSelectedYear1}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Año" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableYears.map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Comparar con</Label>
                      <Select value={selectedYear2} onValueChange={setSelectedYear2}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Ninguno" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Ninguno</SelectItem>
                          {availableYears.filter(y => y.toString() !== selectedYear1).map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Comparar con</Label>
                      <Select value={selectedYear3} onValueChange={setSelectedYear3}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Ninguno" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Ninguno</SelectItem>
                          {availableYears.filter(y => y.toString() !== selectedYear1 && y.toString() !== selectedYear2).map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 ml-auto">
                      {selectedYear1 && (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                          <div className="w-3 h-3 rounded-full bg-emerald-500 mr-2"></div>
                          {selectedYear1}
                        </Badge>
                      )}
                      {selectedYear2 && selectedYear2 !== 'none' && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                          <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                          {selectedYear2}
                        </Badge>
                      )}
                      {selectedYear3 && selectedYear3 !== 'none' && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                          <div className="w-3 h-3 rounded-full bg-amber-500 mr-2"></div>
                          {selectedYear3}
                        </Badge>
                      )}
                    </div>
                    {canEditLiquidaciones && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowLiquidacionesModal(true)}
                        className="ml-auto text-purple-700 border-purple-200 hover:bg-purple-50"
                        data-testid="btn-cargar-liquidaciones"
                      >
                        <Package className="w-4 h-4 mr-1.5" />
                        Cargar liquidaciones históricas
                      </Button>
                    )}
                  </div>
                  
                  {/* KPIs Comparativos Año-vs-Año */}
                  {kpisYoY && (kpisYoY.total.curr > 0 || kpisYoY.total.prev > 0) && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {[
                        { label: 'TM Total', data: kpisYoY.total, color: 'emerald', unit: 'TM' },
                        { label: 'TM Fruta', data: kpisYoY.fruta, color: 'green', unit: 'TM' },
                        { label: 'TM Coyol', data: kpisYoY.coyol, color: 'teal', unit: 'TM' },
                        { label: 'Ingresos', data: kpisYoY.ingreso, color: 'purple', unit: '₡' },
                      ].map((kpi, i) => {
                        const delta = kpi.data.delta;
                        const up = delta !== null && delta >= 0;
                        const hasData = kpi.data.curr > 0 || kpi.data.prev > 0;
                        if (!hasData && kpi.unit === '₡') return null;
                        const format = (v) => kpi.unit === '₡'
                          ? `₡${v >= 1_000_000 ? (v / 1_000_000).toFixed(1) + 'M' : v.toLocaleString('es-CR', { maximumFractionDigits: 0 })}`
                          : `${v.toFixed(1)} ${kpi.unit}`;
                        return (
                          <Card key={i} className="border-l-4" style={{ borderLeftColor: up ? '#10b981' : '#ef4444' }} data-testid={`kpi-yoy-${kpi.label.toLowerCase().replace(' ', '-')}`}>
                            <CardContent className="p-3">
                              <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
                                {kpi.label} {kpisYoY.isYTD ? `YTD` : ''} · {kpisYoY.yearCurr}
                              </p>
                              <p className="text-lg font-bold text-gray-900 mt-0.5">{format(kpi.data.curr)}</p>
                              <div className="flex items-center gap-1 mt-1 text-xs">
                                {delta !== null ? (
                                  <>
                                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-semibold ${up ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                      {up ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
                                    </span>
                                    <span className="text-gray-500">vs {kpisYoY.yearPrev} ({format(kpi.data.prev)})</span>
                                  </>
                                ) : (
                                  <span className="text-gray-400">Sin datos {kpisYoY.yearPrev}</span>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  {/* Gráfico de Barras - Tonelaje por Mes */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-emerald-600" />
                        Tonelaje Mensual - Fruta vs Coyol
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} label={{ value: 'TM', angle: -90, position: 'insideLeft' }} />
                            <Tooltip 
                              formatter={(value, name) => {
                                const parts = name.split('_');
                                const tipo = parts[0] === 'fruta' ? 'Fruta' : 'Coyol';
                                const year = parts[1];
                                return [`${value} TM`, `${tipo} ${year}`];
                              }}
                              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                            />
                            <Legend 
                              formatter={(value) => {
                                const parts = value.split('_');
                                const tipo = parts[0] === 'fruta' ? 'Fruta' : 'Coyol';
                                const year = parts[1];
                                return `${tipo} ${year}`;
                              }}
                            />
                            {selectedYear1 && (
                              <>
                                <Bar dataKey={`fruta_${selectedYear1}`} fill="#10b981" name={`fruta_${selectedYear1}`} />
                                <Bar dataKey={`coyol_${selectedYear1}`} fill="#059669" name={`coyol_${selectedYear1}`} />
                              </>
                            )}
                            {selectedYear2 && selectedYear2 !== 'none' && (
                              <>
                                <Bar dataKey={`fruta_${selectedYear2}`} fill="#3b82f6" name={`fruta_${selectedYear2}`} />
                                <Bar dataKey={`coyol_${selectedYear2}`} fill="#1d4ed8" name={`coyol_${selectedYear2}`} />
                              </>
                            )}
                            {selectedYear3 && selectedYear3 !== 'none' && (
                              <>
                                <Bar dataKey={`fruta_${selectedYear3}`} fill="#f59e0b" name={`fruta_${selectedYear3}`} />
                                <Bar dataKey={`coyol_${selectedYear3}`} fill="#d97706" name={`coyol_${selectedYear3}`} />
                              </>
                            )}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Gráfico de Líneas - Tendencia Combinada */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <LineChart className="w-5 h-5 text-blue-600" />
                        Tendencia de Producción (Fruta y Coyol)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsLineChart 
                            data={chartData} 
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            onMouseLeave={() => setActiveLineKey(null)}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} label={{ value: 'TM', angle: -90, position: 'insideLeft' }} />
                            <Tooltip 
                              content={({ active, payload, label }) => {
                                if (!active || !payload || payload.length === 0) return null;
                                
                                // Si hay una línea activa, filtrar solo esos datos
                                const filteredPayload = activeLineKey 
                                  ? payload.filter(item => item.dataKey === activeLineKey)
                                  : payload;
                                
                                if (filteredPayload.length === 0) return null;
                                
                                // Si es una sola línea (cursor sobre línea específica)
                                if (activeLineKey) {
                                  const item = filteredPayload[0];
                                  const parts = item.dataKey.split('_');
                                  const tipo = parts[0] === 'fruta' ? 'TM Fruta' : 'TM Coyol';
                                  const year = parts[1];
                                  
                                  return (
                                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                      <p className="font-semibold text-gray-800 mb-1">{label} {year}</p>
                                      <div className="flex items-center gap-2">
                                        <span style={{ color: item.stroke }}>
                                          {parts[0] === 'fruta' ? '●' : '◆'}
                                        </span>
                                        <span className="text-sm">{tipo}:</span>
                                        <span className="font-bold">{item.value?.toFixed(2)}</span>
                                      </div>
                                    </div>
                                  );
                                }
                                
                                // Si no hay línea activa, mostrar todo agrupado por año
                                const dataByYear = {};
                                filteredPayload.forEach(item => {
                                  const parts = item.dataKey.split('_');
                                  const tipo = parts[0];
                                  const year = parts[1];
                                  if (!dataByYear[year]) {
                                    dataByYear[year] = { fruta: 0, coyol: 0 };
                                  }
                                  dataByYear[year][tipo] = item.value || 0;
                                });
                                
                                return (
                                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                    {Object.entries(dataByYear).map(([year, data]) => {
                                      const total = (data.fruta || 0) + (data.coyol || 0);
                                      const yColors = {
                                        [selectedYear1]: { fruta: '#10b981', coyol: '#059669' },
                                        [selectedYear2]: { fruta: '#3b82f6', coyol: '#1d4ed8' },
                                        [selectedYear3]: { fruta: '#f59e0b', coyol: '#d97706' },
                                      };
                                      const colors = yColors[year] || { fruta: '#10b981', coyol: '#059669' };
                                      
                                      return (
                                        <div key={year} className="mb-2 last:mb-0">
                                          <p className="font-semibold text-gray-800 mb-1">{label} {year}</p>
                                          <div className="space-y-1 text-sm">
                                            <div className="flex justify-between items-center">
                                              <span style={{ color: colors.fruta }}>● TM Fruta:</span>
                                              <span className="font-medium ml-3">{data.fruta?.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                              <span style={{ color: colors.coyol }}>◆ TM Coyol:</span>
                                              <span className="font-medium ml-3">{data.coyol?.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between items-center border-t pt-1 mt-1">
                                              <span className="text-gray-600 font-medium">TM Total:</span>
                                              <span className="font-bold ml-3">{total.toFixed(2)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              }}
                            />
                            <Legend 
                              formatter={(value) => {
                                const parts = value.split('_');
                                const tipo = parts[0] === 'fruta' ? 'Fruta' : 'Coyol';
                                const year = parts[1];
                                return `${tipo} ${year}`;
                              }}
                            />
                            {/* Líneas de Fruta */}
                            {selectedYear1 && (
                              <Line 
                                type="monotone" 
                                dataKey={`fruta_${selectedYear1}`} 
                                stroke="#10b981" 
                                strokeWidth={activeLineKey === `fruta_${selectedYear1}` ? 4 : 2}
                                dot={{ fill: '#10b981', strokeWidth: 2 }}
                                name={`fruta_${selectedYear1}`}
                                onMouseEnter={() => setActiveLineKey(`fruta_${selectedYear1}`)}
                                onMouseLeave={() => setActiveLineKey(null)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            {selectedYear2 && selectedYear2 !== 'none' && (
                              <Line 
                                type="monotone" 
                                dataKey={`fruta_${selectedYear2}`} 
                                stroke="#3b82f6" 
                                strokeWidth={activeLineKey === `fruta_${selectedYear2}` ? 4 : 2}
                                dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                                name={`fruta_${selectedYear2}`}
                                onMouseEnter={() => setActiveLineKey(`fruta_${selectedYear2}`)}
                                onMouseLeave={() => setActiveLineKey(null)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            {selectedYear3 && selectedYear3 !== 'none' && (
                              <Line 
                                type="monotone" 
                                dataKey={`fruta_${selectedYear3}`} 
                                stroke="#f59e0b" 
                                strokeWidth={activeLineKey === `fruta_${selectedYear3}` ? 4 : 2}
                                dot={{ fill: '#f59e0b', strokeWidth: 2 }}
                                name={`fruta_${selectedYear3}`}
                                onMouseEnter={() => setActiveLineKey(`fruta_${selectedYear3}`)}
                                onMouseLeave={() => setActiveLineKey(null)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            {/* Líneas de Coyol (líneas punteadas para diferenciar) */}
                            {selectedYear1 && (
                              <Line 
                                type="monotone" 
                                dataKey={`coyol_${selectedYear1}`} 
                                stroke="#059669" 
                                strokeWidth={activeLineKey === `coyol_${selectedYear1}` ? 4 : 2}
                                strokeDasharray="5 5"
                                dot={{ fill: '#059669', strokeWidth: 2 }}
                                name={`coyol_${selectedYear1}`}
                                onMouseEnter={() => setActiveLineKey(`coyol_${selectedYear1}`)}
                                onMouseLeave={() => setActiveLineKey(null)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            {selectedYear2 && selectedYear2 !== 'none' && (
                              <Line 
                                type="monotone" 
                                dataKey={`coyol_${selectedYear2}`} 
                                stroke="#1d4ed8" 
                                strokeWidth={activeLineKey === `coyol_${selectedYear2}` ? 4 : 2}
                                strokeDasharray="5 5"
                                dot={{ fill: '#1d4ed8', strokeWidth: 2 }}
                                name={`coyol_${selectedYear2}`}
                                onMouseEnter={() => setActiveLineKey(`coyol_${selectedYear2}`)}
                                onMouseLeave={() => setActiveLineKey(null)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            {selectedYear3 && selectedYear3 !== 'none' && (
                              <Line 
                                type="monotone" 
                                dataKey={`coyol_${selectedYear3}`} 
                                stroke="#d97706" 
                                strokeWidth={activeLineKey === `coyol_${selectedYear3}` ? 4 : 2}
                                strokeDasharray="5 5"
                                dot={{ fill: '#d97706', strokeWidth: 2 }}
                                name={`coyol_${selectedYear3}`}
                                onMouseEnter={() => setActiveLineKey(`coyol_${selectedYear3}`)}
                                onMouseLeave={() => setActiveLineKey(null)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </RechartsLineChart>
                        </ResponsiveContainer>
                      </div>
                      <p className="text-xs text-gray-500 text-center mt-2">
                        Línea continua = Fruta | Línea punteada = Coyol | Pasa el cursor sobre una línea para ver solo sus datos
                      </p>
                    </CardContent>
                  </Card>

                  {/* Gráfico de Ingresos por mes (desde liquidaciones con precio) */}
                  {liquidaciones && liquidaciones.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-emerald-600" />
                          Ingresos por Mes (₡)
                        </CardTitle>
                        <p className="text-xs text-gray-500 mt-1">Basado en liquidaciones mensuales cargadas con precio por tonelada</p>
                      </CardHeader>
                      <CardContent>
                        <div className="h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={ingresosChartData} margin={{ top: 20, right: 30, left: 40, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                              <YAxis
                                tick={{ fontSize: 11 }}
                                tickFormatter={(v) => `₡${(v / 1000).toFixed(0)}k`}
                              />
                              <Tooltip
                                formatter={(value, name) => {
                                  const parts = name.split('_');
                                  const tipo = parts[1] === 'fruta' ? 'Ingreso Fruta' : 'Ingreso Coyol';
                                  const year = parts[2];
                                  return [`₡${Number(value).toLocaleString('es-CR')}`, `${tipo} ${year}`];
                                }}
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                              />
                              <Legend
                                formatter={(value) => {
                                  const parts = value.split('_');
                                  const tipo = parts[1] === 'fruta' ? 'Fruta' : 'Coyol';
                                  return `${tipo} ${parts[2]}`;
                                }}
                              />
                              {selectedYear1 && (
                                <>
                                  <Bar dataKey={`ingreso_fruta_${selectedYear1}`} stackId={`ing_${selectedYear1}`} fill="#10b981" />
                                  <Bar dataKey={`ingreso_coyol_${selectedYear1}`} stackId={`ing_${selectedYear1}`} fill="#059669" />
                                </>
                              )}
                              {selectedYear2 && selectedYear2 !== 'none' && (
                                <>
                                  <Bar dataKey={`ingreso_fruta_${selectedYear2}`} stackId={`ing_${selectedYear2}`} fill="#3b82f6" />
                                  <Bar dataKey={`ingreso_coyol_${selectedYear2}`} stackId={`ing_${selectedYear2}`} fill="#1d4ed8" />
                                </>
                              )}
                              {selectedYear3 && selectedYear3 !== 'none' && (
                                <>
                                  <Bar dataKey={`ingreso_fruta_${selectedYear3}`} stackId={`ing_${selectedYear3}`} fill="#f59e0b" />
                                  <Bar dataKey={`ingreso_coyol_${selectedYear3}`} stackId={`ing_${selectedYear3}`} fill="#d97706" />
                                </>
                              )}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        </motion.div>
      ) : (
        <motion.div
          key="formulario"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
        >
        <Card>
          <CardHeader>
            <CardTitle>
              {editMode ? 'Editar' : 'Nuevo'} Registro de Boleta - {getStepTitle()}
            </CardTitle>
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  Paso {currentStep} de 4
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all"
                  style={{ width: `${(currentStep / 4) * 100}%` }}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <AnimatePresence mode="wait">
            <motion.div 
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {/* PASO 1: DATOS GENERALES */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Usuario:</strong> {formData.usuario_registro} | <strong>Fecha de Registro:</strong> {new Date().toLocaleString('es-ES', { 
                        year: 'numeric', 
                        month: '2-digit', 
                        day: '2-digit', 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false 
                      })}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>No. de Tiquete (últimos 5 dígitos) *</Label>
                      <Input
                        value={formData.numero_tiquete}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                          setFormData({ ...formData, numero_tiquete: value });
                        }}
                        placeholder="12345"
                        maxLength={5}
                        className={boletaDuplicada ? 'border-red-500 focus:border-red-500' : ''}
                      />
                      {validandoBoleta && formData.numero_tiquete.length >= 3 && (
                        <p className="text-sm text-gray-500 flex items-center">
                          <span className="animate-spin mr-2">⌛</span>
                          Validando...
                        </p>
                      )}
                      {boletaDuplicada && !validandoBoleta && (
                        <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-red-800">
                              ¡Boleta Duplicada!
                            </p>
                            <p className="text-xs text-red-700 mt-1">
                              El No. de Tiquete <strong>{formData.numero_tiquete}</strong> ya fue registrado anteriormente.
                              Por favor, verifica el número o usa la función de búsqueda para encontrar el registro existente.
                            </p>
                          </div>
                        </div>
                      )}
                      {!boletaDuplicada && !validandoBoleta && formData.numero_tiquete.length === 5 && (
                        <p className="text-sm text-green-600 flex items-center">
                          <Check className="w-4 h-4 mr-1" />
                          Número disponible
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Fecha de Entrega *</Label>
                      <DateInput
                        value={formData.fecha_entrega}
                        onChange={(value) => setFormData({ ...formData, fecha_entrega: value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Hora de Ingreso (Fecha Bruto) *</Label>
                      <Input
                        type="time"
                        value={formData.hora_ingreso}
                        onChange={(e) => setFormData({ ...formData, hora_ingreso: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Hora de Salida (Fecha Tara) *</Label>
                      <Input
                        type="time"
                        value={formData.hora_salida}
                        onChange={(e) => setFormData({ ...formData, hora_salida: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* PASO 2: PRODUCTOR */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre del Conductor *</Label>
                    <Select
                      value={formData.conductor_id}
                      onValueChange={(value) => {
                        const trabajador = trabajadores.find(t => t.id === value);
                        setFormData({
                          ...formData,
                          conductor_id: value,
                          conductor_nombre: trabajador ? trabajador.nombre_completo : '',
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un conductor" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          // Filtrar choferes activos
                          const choferesActivos = trabajadores.filter(
                            trab => trab.ocupacion === 'Chofer' && trab.status === 'Activo'
                          );
                          
                          // Si el usuario tiene trabajador_id y es chofer, solo mostrar a sí mismo
                          if (user.trabajador_id) {
                            const userTrabajador = trabajadores.find(t => t.id === user.trabajador_id);
                            if (userTrabajador && userTrabajador.ocupacion === 'Chofer' && userTrabajador.status === 'Activo') {
                              return (
                                <SelectItem key={userTrabajador.id} value={userTrabajador.id}>
                                  {userTrabajador.nombre_completo}
                                </SelectItem>
                              );
                            }
                          }
                          
                          // Si no es chofer o no tiene trabajador_id, mostrar todos los choferes
                          return choferesActivos.map((trab) => (
                            <SelectItem key={trab.id} value={trab.id}>
                              {trab.nombre_completo}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                    {trabajadores.filter(t => t.ocupacion === 'Chofer' && t.status === 'Activo').length === 0 && (
                      <p className="text-sm text-amber-600">
                        No hay trabajadores registrados con ocupación &quot;Chofer&quot;. Por favor, registre uno primero.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Vehículo *</Label>
                    <Select
                      value={formData.vehiculo_id}
                      onValueChange={(value) => {
                        const vehiculo = vehiculos.find(v => v.id === value);
                        setFormData({
                          ...formData,
                          vehiculo_id: value,
                          vehiculo_placa: vehiculo ? vehiculo.placa : '',
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un vehículo" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehiculos.filter(veh => veh.estado_actual === 'Activo').length === 0 ? (
                          <div className="p-2 text-center text-sm text-gray-500">
                            No hay vehículos activos disponibles
                          </div>
                        ) : (
                          vehiculos
                            .filter(veh => veh.estado_actual === 'Activo')
                            .map((veh) => (
                              <SelectItem key={veh.id} value={veh.id}>
                                {veh.placa} - {veh.marca} {veh.modelo} ({veh.tipo_vehiculo})
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Trazabilidad de Fruta */}
                  <div className="space-y-2">
                    <Label>Trazabilidad de Fruta {!editMode && '*'}</Label>
                    <Select
                      value={formData.trazabilidad_fruta}
                      onValueChange={(value) => setFormData({ ...formData, trazabilidad_fruta: value === '_none' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione trazabilidad" />
                      </SelectTrigger>
                      <SelectContent>
                        {editMode && (
                          <SelectItem value="_none">Sin selección</SelectItem>
                        )}
                        <SelectItem value="Lote 1 (Área PNMA)">Lote 1 (Área PNMA)</SelectItem>
                        <SelectItem value="Lote 2 (Área sin afectación)">Lote 2 (Área sin afectación)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* PASO 3: PRODUCTO */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  {/* Racimo */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-4">Racimo</h3>
                    <div className="grid grid-cols-3 gap-4 items-end">
                      <div className="space-y-2">
                        <Label>Peso Fruta</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.peso_fruta}
                          onChange={(e) => setFormData({ ...formData, peso_fruta: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cantidad de Racimos</Label>
                        <Input
                          type="number"
                          value={formData.cantidad_racimos}
                          onChange={(e) => setFormData({ ...formData, cantidad_racimos: e.target.value })}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>PPR (Peso Promedio Racimo)</Label>
                        <div className="text-2xl font-bold text-emerald-600">
                          {formData.ppr || '0.00'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coyol */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-4">Coyol</h3>
                    <div className="grid grid-cols-4 gap-4 items-end">
                      <div className="space-y-2">
                        <Label>Peso Coyol</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.peso_coyol}
                          onChange={(e) => setFormData({ ...formData, peso_coyol: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cantidad de Sacos</Label>
                        <Input
                          type="number"
                          value={formData.cantidad_sacos}
                          onChange={(e) => setFormData({ ...formData, cantidad_sacos: e.target.value })}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>% Coyol</Label>
                        <div className={`text-2xl font-bold ${parseFloat(formData.porcentaje_coyol) < 5 && parseFloat(formData.porcentaje_coyol) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {formData.porcentaje_coyol || '0.00'}%
                        </div>
                        {parseFloat(formData.porcentaje_coyol) < 5 && parseFloat(formData.porcentaje_coyol) > 0 && (
                          <div className="flex items-center text-red-600 text-sm mt-1">
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            El % Coyol es inferior al 5%
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>PPS (Peso Promedio Saco)</Label>
                        <div className={`text-2xl font-bold ${parseFloat(formData.pps) < 20 && parseFloat(formData.pps) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {formData.pps || '0.00'}
                        </div>
                        {parseFloat(formData.pps) < 20 && parseFloat(formData.pps) > 0 && (
                          <div className="flex items-center text-red-600 text-sm mt-1">
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            El PPS es inferior a 20
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pesos */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Pesos</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Peso Neto (calculado)</Label>
                        <Input
                          type="number"
                          value={formData.peso_neto}
                          disabled
                          className="bg-gray-100"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Peso Tara *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.peso_tara}
                          onChange={(e) => setFormData({ ...formData, peso_tara: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Peso Bruto (calculado)</Label>
                        <Input
                          type="number"
                          value={formData.peso_bruto}
                          disabled
                          className="bg-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* PASO 4: CONTROL DE CALIDAD */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Cantidad Racimos Evaluados *</Label>
                    <Input
                      type="number"
                      value={formData.cantidad_racimos_evaluados}
                      onChange={(e) => {
                        setFormData({ ...formData, cantidad_racimos_evaluados: e.target.value });
                      }}
                      placeholder="0"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cantidad Racimos Pasados</Label>
                      <Input
                        type="number"
                        value={formData.cantidad_racimos_pasados}
                        onChange={(e) => calculatePercentages('cantidad_racimos_pasados', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>% Racimos Pasados</Label>
                      <Input
                        type="number"
                        value={formData.porcentaje_racimos_pasados}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cantidad Pinzote Largo</Label>
                      <Input
                        type="number"
                        value={formData.cantidad_pinzote_largo}
                        onChange={(e) => calculatePercentages('cantidad_pinzote_largo', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>% Pinzote Largo</Label>
                      <Input
                        type="number"
                        value={formData.porcentaje_pinzote_largo}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cantidad Racimos Verdes</Label>
                      <Input
                        type="number"
                        value={formData.cantidad_racimos_verdes}
                        onChange={(e) => calculatePercentages('cantidad_racimos_verdes', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>% Racimos Verdes</Label>
                      <Input
                        type="number"
                        value={formData.porcentaje_racimos_verdes}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cantidad Racimos Sobre Maduros</Label>
                      <Input
                        type="number"
                        value={formData.cantidad_racimos_sobre_maduros}
                        onChange={(e) => calculatePercentages('cantidad_racimos_sobre_maduros', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>% Racimos Sobre Maduros</Label>
                      <Input
                        type="number"
                        value={formData.porcentaje_racimos_sobre_maduros}
                        disabled
                        className="bg-gray-100"
                      />
                      <Badge className="mt-1 bg-blue-100 text-blue-800">
                        No cuenta para penalización de calidad
                      </Badge>
                    </div>
                  </div>

                  {/* Inspectores */}
                  <div className="space-y-2">
                    <Label>Romanero *</Label>
                    <div className="flex gap-2">
                      <Select
                        value={formData.inspector}
                        onValueChange={(value) => setFormData({ ...formData, inspector: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione un romanero" />
                        </SelectTrigger>
                        <SelectContent>
                          {inspectores.map((insp) => (
                            <SelectItem key={insp.id} value={insp.nombre_completo}>
                              {insp.nombre_completo}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        onClick={() => setShowNuevoInspectorModal(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar nuevo Inspector
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Inspector (Opcional)</Label>
                    <div className="flex gap-2">
                      <Select
                        value={formData.inspector_2 || '__none__'}
                        onValueChange={(value) => {
                          if (value === '__none__') {
                            setFormData({ ...formData, inspector_2: '' });
                          } else {
                            setFormData({ ...formData, inspector_2: value });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Ninguno" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Ninguno</SelectItem>
                          {inspectores.map((insp) => (
                            <SelectItem key={insp.id} value={insp.nombre_completo}>
                              {insp.nombre_completo}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.inspector_2 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({ ...formData, inspector_2: '' })}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* División */}
                  <div className="space-y-4 mt-6">
                    <Label>División - Palmatica *</Label>
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant={formData.division === 'División Quepos - NARANJO' ? 'default' : 'outline'}
                        className={`w-full ${formData.division === 'División Quepos - NARANJO' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                        onClick={() => setFormData({ ...formData, division: 'División Quepos - NARANJO' })}
                      >
                        División Quepos - NARANJO
                      </Button>
                      <Button
                        type="button"
                        variant={formData.division === 'División Quepos - PALO SECO' ? 'default' : 'outline'}
                        className={`w-full ${formData.division === 'División Quepos - PALO SECO' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                        onClick={() => setFormData({ ...formData, division: 'División Quepos - PALO SECO' })}
                      >
                        División Quepos - PALO SECO
                      </Button>
                    </div>
                  </div>

                  {/* Imagen de la Boleta */}
                  {formData.division && (
                    <div className="space-y-2 mt-6">
                      <Label htmlFor="imagen_boleta">Foto de la Boleta</Label>
                      <p className="text-xs text-gray-500 mb-2">
                        Puedes tomar una foto o seleccionar desde la galería
                      </p>
                      
                      {/* Mostrar imagen existente en modo edición */}
                      {editMode && formData.imagen_boleta && !imagenBoletaPreview && (
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
                          <p className="text-sm font-medium text-gray-700 mb-2">Imagen actual:</p>
                          <div className="flex items-center gap-3">
                            <img 
                              src={formData.imagen_boleta.startsWith('https://') 
                                ? formData.imagen_boleta 
                                : `${process.env.REACT_APP_BACKEND_URL}/api${formData.imagen_boleta}`
                              } 
                              alt="Boleta actual" 
                              className="h-24 w-auto object-contain rounded border"
                            />
                            <div className="flex flex-col gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const url = formData.imagen_boleta.startsWith('https://') 
                                    ? formData.imagen_boleta 
                                    : `${process.env.REACT_APP_BACKEND_URL}/api${formData.imagen_boleta}`;
                                  window.open(url, '_blank');
                                }}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Ver completa
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => setFormData(prev => ({ ...prev, imagen_boleta: null }))}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Eliminar
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Sube una nueva imagen para reemplazar la actual
                          </p>
                        </div>
                      )}
                      
                      <Input
                        id="imagen_boleta"
                        type="file"
                        accept="image/*"
                        onChange={handleImagenBoletaChange}
                        className="cursor-pointer"
                      />
                      {imagenBoletaPreview && (
                        <div className="mt-3">
                          <img 
                            src={imagenBoletaPreview} 
                            alt="Preview de boleta" 
                            className="max-w-full h-auto rounded-lg border-2 border-gray-200 max-h-64 object-contain"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setImagenBoletaFile(null);
                              setImagenBoletaPreview(null);
                              // Limpiar el input file
                              const fileInput = document.getElementById('imagen_boleta');
                              if (fileInput) fileInput.value = '';
                            }}
                            className="mt-2"
                          >
                            Quitar imagen nueva
                          </Button>
                        </div>
                      )}
                      {compressingImage && (
                        <p className="text-sm text-amber-600 flex items-center">
                          <span className="animate-spin mr-2">🔄</span>
                          Optimizando imagen...
                        </p>
                      )}
                      {uploadingImage && (
                        <p className="text-sm text-blue-600 flex items-center">
                          <span className="animate-spin mr-2">⏳</span>
                          Subiendo imagen, por favor espera...
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Campo de Observaciones */}
                  {formData.division && (
                    <div className="space-y-2 mt-6">
                      <Label htmlFor="observaciones">Observaciones</Label>
                      <p className="text-xs text-gray-500 mb-2">
                        Opcional: anota discrepancias entre la foto y los datos reales, correcciones u otras notas
                      </p>
                      <Input
                        id="observaciones"
                        type="text"
                        placeholder="Ej: Se modificó el peso porque la boleta original tenía un error..."
                        value={formData.observaciones}
                        onChange={(e) => handleInputChange('observaciones', e.target.value)}
                        className="w-full"
                        maxLength={500}
                      />
                      {formData.observaciones && (
                        <p className="text-xs text-gray-400 text-right">
                          {formData.observaciones.length}/500 caracteres
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
            </AnimatePresence>

            {/* BOTONES DE NAVEGACIÓN */}
            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                onClick={() => {
                  if (currentStep === 1) {
                    resetForm();
                  } else {
                    handleBack();
                  }
                }}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                {currentStep === 1 ? 'Cancelar' : 'Anterior'}
              </Button>

              {currentStep < 4 ? (
                <Button 
                  onClick={handleNext} 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={currentStep === 1 && boletaDuplicada}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={uploadingImage || compressingImage}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Guardar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Modal para agregar nuevo inspector */}
      <Dialog open={showNuevoInspectorModal} onOpenChange={setShowNuevoInspectorModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Nuevo Inspector</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={nuevoInspector.nombre}
                onChange={(e) => setNuevoInspector({ ...nuevoInspector, nombre: e.target.value })}
                placeholder="Ingrese el nombre"
              />
            </div>
            <div className="space-y-2">
              <Label>Primer Apellido (Opcional)</Label>
              <Input
                value={nuevoInspector.primer_apellido}
                onChange={(e) => setNuevoInspector({ ...nuevoInspector, primer_apellido: e.target.value })}
                placeholder="Ingrese el primer apellido"
              />
            </div>
            <div className="space-y-2">
              <Label>Segundo Apellido (Opcional)</Label>
              <Input
                value={nuevoInspector.segundo_apellido}
                onChange={(e) => setNuevoInspector({ ...nuevoInspector, segundo_apellido: e.target.value })}
                placeholder="Ingrese el segundo apellido"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowNuevoInspectorModal(false);
                setNuevoInspector({ nombre: '', primer_apellido: '', segundo_apellido: '' });
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddNewInspector}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmación Eliminación Masiva */}
      <Dialog open={showDeleteMasivoModal} onOpenChange={setShowDeleteMasivoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <Trash2 className="w-5 h-5 mr-2" />
              Confirmar Eliminación Masiva
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    ¿Está seguro de eliminar {selectedHojas.length} registro(s)?
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Esta acción no se puede deshacer. Todos los registros seleccionados serán eliminados permanentemente.
                  </p>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-600">
              Se eliminarán los registros con los siguientes números de boleta:
            </p>
            <div className="mt-2 max-h-32 overflow-y-auto bg-gray-50 rounded p-2">
              <div className="flex flex-wrap gap-1">
                {selectedHojas.map(hojaId => {
                  const hoja = hojasFiltradas.find(h => h.id === hojaId);
                  return hoja ? (
                    <Badge key={hojaId} variant="outline" className="text-xs">
                      #{hoja.numero_tiquete}
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteMasivoModal(false)}
              disabled={deletingMasivo}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMasivo}
              disabled={deletingMasivo}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingMasivo ? (
                <>
                  <span className="animate-spin mr-2">⌛</span>
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar {selectedHojas.length} registro(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Exportar PDF */}
      <Dialog open={showExportarModal} onOpenChange={setShowExportarModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <FileDown className="w-5 h-5 mr-2 text-emerald-600" />
              Exportar Hoja del Pesador a PDF
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Filtros */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-gray-700">Filtros (Opcional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Desde</Label>
                  <Input
                    type="date"
                    value={filtrosExportacion.fecha_desde}
                    onChange={(e) => setFiltrosExportacion({...filtrosExportacion, fecha_desde: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Hasta</Label>
                  <Input
                    type="date"
                    value={filtrosExportacion.fecha_hasta}
                    onChange={(e) => setFiltrosExportacion({...filtrosExportacion, fecha_hasta: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Conductor (Opcional)</Label>
                  <Select 
                    value={filtrosExportacion.conductor_id || "__todos__"} 
                    onValueChange={(value) => setFiltrosExportacion({...filtrosExportacion, conductor_id: value === "__todos__" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos los conductores" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__todos__">Todos</SelectItem>
                      {trabajadores.filter(t => t.ocupacion === 'Chofer').map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.nombre} {t.primer_apellido}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>División (Opcional)</Label>
                  <Select 
                    value={filtrosExportacion.division || "__todas__"} 
                    onValueChange={(value) => setFiltrosExportacion({...filtrosExportacion, division: value === "__todas__" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todas las divisiones" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__todas__">Todas</SelectItem>
                      <SelectItem value="División Quepos - NARANJO">División Quepos - NARANJO</SelectItem>
                      <SelectItem value="División Quepos - PALO SECO">División Quepos - PALO SECO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Selección de Columnas */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-gray-700">Columnas a Incluir *</h3>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={seleccionarColumnasFavoritas}
                    className="text-xs border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                    title="Seleccionar columnas favoritas"
                  >
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-600" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={seleccionarTodasColumnas}
                    className="text-xs"
                  >
                    Seleccionar Todas
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={deseleccionarTodasColumnas}
                    className="text-xs"
                  >
                    Deseleccionar Todas
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3 max-h-60 overflow-y-auto border p-4 rounded-md bg-gray-50">
                {columnasDisponibles.map(columna => (
                  <label key={columna.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={columnasSeleccionadas.includes(columna.id)}
                      onChange={() => toggleColumna(columna.id)}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm">{columna.label}</span>
                  </label>
                ))}
              </div>
              
              {columnasSeleccionadas.length > 0 && (
                <p className="text-xs text-gray-500">
                  {columnasSeleccionadas.length} columna(s) seleccionada(s)
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setShowExportarModal(false)}
              disabled={exportLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleExportarPDF}
              disabled={exportLoading || columnasSeleccionadas.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {exportLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Generando...
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Generar PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Historial de Cambios */}
      <Dialog open={showHistorialModal} onOpenChange={setShowHistorialModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              Historial de Boleta #{historialBoletaNumero}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {loadingHistorial ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-gray-500">Cargando historial...</span>
              </div>
            ) : historialBoleta.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No hay cambios registrados para esta boleta.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {historialBoleta.map((item, index) => (
                  <div key={item.id || index} className="border-l-4 border-purple-500 pl-4 py-2 bg-gray-50 rounded-r-lg">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">
                        {item.usuario_nombre}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        item.accion === 'crear' ? 'bg-green-100 text-green-700' :
                        item.accion === 'editar' ? 'bg-blue-100 text-blue-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {item.accion === 'crear' ? 'Creó el registro' : 
                         item.accion === 'editar' ? 'Editó' : 'Eliminó'}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {new Date(item.fecha).toLocaleString('es-CR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    {item.cambios && item.cambios.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {item.cambios.map((cambio, idx) => (
                          <div key={idx} className="text-sm flex items-center gap-2 bg-white p-2 rounded border">
                            <span className="font-medium text-gray-700">{cambio.campo}:</span>
                            <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded line-through">
                              {cambio.valor_anterior || 'vacío'}
                            </span>
                            <span className="text-gray-400">→</span>
                            <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded font-medium">
                              {cambio.valor_nuevo || 'vacío'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowHistorialModal(false)}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Cargar Liquidaciones Históricas */}
      <Dialog open={showLiquidacionesModal} onOpenChange={setShowLiquidacionesModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="modal-liquidaciones">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" />
              Cargar Liquidaciones Históricas de PalmaTica
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Ingresa las toneladas y precios que te da PalmaTica en su liquidación mensual. Los datos se combinan con las boletas reales en los gráficos.
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-3">
              <Label className="text-sm font-medium">Año:</Label>
              <Select value={liqEditingYear.toString()} onValueChange={handleYearChange}>
                <SelectTrigger className="w-32" data-testid="liq-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {historicalYears.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Solo puedes cargar años anteriores al actual ({currentYear})</p>
              {isLiqDirty && (
                <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1" data-testid="liq-dirty-badge">
                  ● Cambios sin guardar
                </span>
              )}
            </div>

            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-sm border border-gray-200 rounded-lg">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-purple-900 w-24">Mes</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-purple-900">TM Fruta</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-purple-900">TM Coyol</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-purple-900 bg-purple-100">TM Total</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-purple-900">₡/TM Fruta</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-purple-900">₡/TM Coyol</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-purple-900">Ajustes ₡ <span className="font-normal text-purple-700">(bonif.)</span></th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-purple-900">Ingreso Total</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {mesesNombres.map((mesNombre, idx) => {
                    const mes = idx + 1;
                    const row = liqRows[mes] || {};
                    const tmF = parseFloat(row.tm_fruta) || 0;
                    const tmC = parseFloat(row.tm_coyol) || 0;
                    const pF = parseFloat(row.precio_tm_fruta) || 0;
                    const pC = parseFloat(row.precio_tm_coyol) || 0;
                    const ajustes = parseFloat(row.ajustes_varios) || 0;
                    const total = (tmF + tmC).toFixed(2);
                    const ingreso = (tmF * pF + tmC * pC + ajustes);
                    const hasData = (liquidaciones || []).some(l => l.año === liqEditingYear && l.mes === mes);

                    return (
                      <tr key={mes} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-800">{mesNombre}</td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={row.tm_fruta}
                            onChange={(e) => setLiqRows(p => ({ ...p, [mes]: { ...p[mes], tm_fruta: e.target.value } }))}
                            className="h-8 w-24 text-sm"
                            placeholder="0"
                            data-testid={`liq-tm-fruta-${mes}`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={row.tm_coyol}
                            onChange={(e) => setLiqRows(p => ({ ...p, [mes]: { ...p[mes], tm_coyol: e.target.value } }))}
                            className="h-8 w-24 text-sm"
                            placeholder="0"
                            data-testid={`liq-tm-coyol-${mes}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-semibold text-purple-900 bg-purple-50">{total}</td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            step="1"
                            value={row.precio_tm_fruta}
                            onChange={(e) => setLiqRows(p => ({ ...p, [mes]: { ...p[mes], precio_tm_fruta: e.target.value } }))}
                            className="h-8 w-28 text-sm"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            step="1"
                            value={row.precio_tm_coyol}
                            onChange={(e) => setLiqRows(p => ({ ...p, [mes]: { ...p[mes], precio_tm_coyol: e.target.value } }))}
                            className="h-8 w-28 text-sm"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex flex-col gap-1">
                            <Input
                              type="number"
                              step="1"
                              value={row.ajustes_varios}
                              onChange={(e) => setLiqRows(p => ({ ...p, [mes]: { ...p[mes], ajustes_varios: e.target.value } }))}
                              className="h-8 w-28 text-sm"
                              placeholder="0"
                              title="Bonificaciones o montos adicionales"
                              data-testid={`liq-ajustes-${mes}`}
                            />
                            {ajustes !== 0 && (
                              <Input
                                type="text"
                                value={row.descripcion_ajustes}
                                onChange={(e) => setLiqRows(p => ({ ...p, [mes]: { ...p[mes], descripcion_ajustes: e.target.value } }))}
                                className="h-6 w-28 text-[10px]"
                                placeholder="Concepto..."
                                data-testid={`liq-desc-ajustes-${mes}`}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {ingreso > 0 ? `₡${ingreso.toLocaleString('es-CR', { maximumFractionDigits: 0 })}` : '—'}
                        </td>
                        <td className="px-2 py-1">
                          {hasData && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteLiqRow(mes)}
                              className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                              title="Eliminar liquidación"
                              data-testid={`liq-delete-${mes}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
              💡 <strong>Tip:</strong> Solo se guardarán los meses con al menos TM Fruta o TM Coyol &gt; 0. Los precios son opcionales pero permiten ver el gráfico de "Ingresos por Mes".
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLiquidacionesModal(false)} data-testid="liq-modal-cancel">
              Cancelar
            </Button>
            <Button
              onClick={handleSaveAllLiquidaciones}
              disabled={savingLiq}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="liq-modal-save"
            >
              {savingLiq ? (
                <><div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2" /> Guardando...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Guardar {liqEditingYear}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal Confirmación al cambiar de año con cambios sin guardar */}
      <Dialog open={pendingYearChange !== null} onOpenChange={(o) => !o && setPendingYearChange(null)}>
        <DialogContent className="sm:max-w-md" data-testid="modal-liq-unsaved">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Cambios sin guardar en {liqEditingYear}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-700 space-y-2">
            <p>Tienes cambios sin guardar en las liquidaciones de <strong>{liqEditingYear}</strong>.</p>
            <p>¿Qué quieres hacer antes de cambiar a <strong>{pendingYearChange}</strong>?</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => handleConfirmYearChange('cancel')}
              data-testid="liq-unsaved-cancel"
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              onClick={() => handleConfirmYearChange('discard')}
              className="text-red-700 border-red-200 hover:bg-red-50"
              data-testid="liq-unsaved-discard"
            >
              Descartar y cambiar
            </Button>
            <Button
              onClick={() => handleConfirmYearChange('save')}
              disabled={savingLiq}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="liq-unsaved-save"
            >
              {savingLiq ? 'Guardando...' : `Guardar ${liqEditingYear} y cambiar`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ProduccionLayout>
  );
}
